import { sanitizeFieldLocations } from "@/lib/fieldLocations";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import DropZone from "../components/DropZone";
import InvoiceList, { type ProcessedFile } from "../components/InvoiceList";
import VatBook from "../components/VatBook";
import { computeSeriesNumbers, listSeries } from "@/lib/invoiceSeries";
import BulkActionsBar, { type BulkPatch } from "../components/BulkActionsBar";
import { mergeFilesToPdf } from "@/lib/pdfMerge";
import GlobalSearch, { type SearchableItem } from "../components/GlobalSearch";
import DocStatusFilter, { type DocStatusFilterValue } from "@/components/DocStatusFilter";
import { sortByDateThenNumber } from "@/lib/invoiceSorting";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  FileStack,
  Download,
  Loader2,
  Play,
  FileText,
  X,
  AlertTriangle,
  RotateCcw,
  Trash2,
  Pencil,
  Check,
  CheckCircle,
  Building2,
  Eye,
  LogOut,
  Search,
  HelpCircle,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { downloadFileAsZip, downloadFilesAsZip, sanitizeFileName } from "@/lib/pdfDownload";
import { loadSupplierMaster, lookupSupplier, loadCompanyMasters, type SupplierMasterEntry, type CompanyMaster } from "@/lib/supplierMaster";
import MasterSelector from "@/components/MasterSelector";
import ThemeToggle from "@/components/ThemeToggle";
import FileViewer from "@/components/FileViewer";
import FailedFileSplitView from "@/components/FailedFileSplitView";
import PdfSplitterDialog from "@/components/PdfSplitterDialog";
import PdfMergeOrderDialog from "@/components/PdfMergeOrderDialog";
import { checkDuplicate, clearContabilizados, getContabilizados } from "@/lib/contabilizado";
import { loadHistoryByNif, matchAgainstHistory, insertHistoryEntries, type InvoiceHistoryEntry } from "@/lib/invoiceHistory";
import { parseNumericLike } from "@/lib/numberFormatting";
import { detectCreditNoteHints } from "@/lib/creditNoteDetection";
import { applyReceivedTaxDefaults, setReceivedOperationType, setReceivedVatDeductibility } from "@/lib/receivedTaxOptions";
import InvoiceTrash from "@/components/InvoiceTrash";
import { useInvoiceSession, type TrashedFile } from "@/hooks/useInvoiceSession";
import { loadLeasingData, matchLeasing, pickInterestAccountByDescription, lookupLeasingDefault } from "@/lib/leasingLookup";
import { loadCompanyAccountCatalog } from "@/lib/companyAccountCatalog";
import {
  loadCompanyPaymentAccounts,
  inferPaymentEntryAccount,
  type CompanyPaymentAccounts,
} from "@/lib/companyPaymentAccounts";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useKeepAwake } from "@/hooks/useKeepAwake";
import type { QuickSupplierCreated } from "@/components/QuickSupplierDialog";

import { recordProcessingEvent } from "@/lib/processingMetrics";
import { requestPermission as requestNotifPermission, notify as browserNotify } from "@/lib/browserNotifications";

import { pdfjsLib, getPdfDocument } from "@/lib/pdfjs";

import type { FailedFile } from "@/components/FailedFileSplitView";
import AIValidatorDemo from "@/components/AIValidatorDemo";

const roundCurrency = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;
const VAT_RATE_OPTIONS = [0, 4, 10, 21] as const;

const toSafeNumber = (value: unknown): number => {
  const numericValue = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(numericValue)) return numericValue;
  const parsedValue = parseNumericLike(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const toSafeText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeInvoiceNumber = (value: unknown): string => {
  const raw = toSafeText(value).toUpperCase();
  if (!raw) return raw;
  // Si el número de factura contiene más de 10 dígitos, nos quedamos
  // solo con los últimos 10 dígitos (descartando letras/separadores).
  const digits = raw.replace(/\D/g, "");
  if (digits.length > 10) return digits.slice(-10);
  return raw;
};

const normalizeVatRate = (value: unknown): number => {
  const roundedRate = Math.round(toSafeNumber(value));
  return VAT_RATE_OPTIONS.includes(roundedRate as (typeof VAT_RATE_OPTIONS)[number]) ? roundedRate : 0;
};


const cloneVatBreakdown = (breakdown?: ProcessedFile["vatBreakdown"]) => {
  const safeBreakdown = Array.isArray(breakdown) && breakdown.length > 0 ? breakdown : [{ taxableBase: 0, vatRate: 0, vatAmount: 0 }];
  return safeBreakdown.map((line) => ({
    taxableBase: toSafeNumber(line?.taxableBase),
    vatRate: normalizeVatRate(line?.vatRate),
    vatAmount: toSafeNumber(line?.vatAmount),
    expenseAccount: typeof line?.expenseAccount === "string" ? line.expenseAccount : undefined,
  }));
};

/**
 * Base sujeta a IRPF: suma únicamente las líneas con % IVA > 0.
 * En compensaciones agrarias al 12 %, la retención se calcula sobre
 * base + compensación. Los suplidos / conceptos exentos no computan.
 */
const computeIrpfTaxableBase = (
  lines: Array<{ taxableBase: number | string; vatRate: number | string; vatAmount?: number | string }> | undefined,
  fallback = 0,
): number => {
  if (!Array.isArray(lines) || lines.length === 0) return fallback;
  const subject = lines.reduce((sum, l) => {
    const rate = toSafeNumber(l?.vatRate);
    if (rate <= 0) return sum;
    const base = toSafeNumber(l?.taxableBase);
    const compensation = Math.abs(rate - 12) < 0.001 ? toSafeNumber(l?.vatAmount) : 0;
    return sum + base + compensation;
  }, 0);
  return subject > 0 ? subject : fallback;
};

const cloneProcessedFileForNumericUpdate = (file: ProcessedFile): ProcessedFile => ({
  ...file,
  vatBreakdown: cloneVatBreakdown(file.vatBreakdown),
  irpfRate: file.irpfRate === undefined ? undefined : toSafeNumber(file.irpfRate),
  irpfAmount: file.irpfAmount === undefined ? undefined : toSafeNumber(file.irpfAmount),
  totalAmount: file.totalAmount === undefined ? undefined : toSafeNumber(file.totalAmount),
  calculatedTotal: file.calculatedTotal === undefined ? undefined : toSafeNumber(file.calculatedTotal),
});

interface PendingFile {
  id: string;
  file: File;
}

// Removed: invoiceWorkMemory global and InvoiceWorkMemory interface
// Session state is now persisted via useInvoiceSession hook

const hashString = (value: string) => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash.toString(36);
};

const createStableId = (seed?: string) => {
  const normalizedSeed = seed?.trim();
  if (normalizedSeed) return `id_${hashString(normalizedSeed)}`;
  return `id_${Math.random().toString(36).slice(2, 10)}`;
};

const getFileStableSeed = (file: File, scope: string, suffix = "") =>
  [scope, file.name, file.size, file.lastModified, file.type, suffix].join(":");

const Index = () => {
  const navigate = useNavigate();
  const { user, isAdmin, canAccessIssuedInvoices, signOut } = useAuth();
  const currentUserId = user?.id ?? "";

  const { loading: sessionLoading, initialState, saveSession, clearSession, saveStatus, lastSavedAt } = useInvoiceSession(currentUserId || undefined);

  const [showMasterSelector, setShowMasterSelector] = useState(false);
  
  // Selector que FIJA el cliente activo de la sesión (gate antes de subir).
  const [showActiveCompanySelector, setShowActiveCompanySelector] = useState(false);
  // Empresa elegida pendiente de confirmación (cuando cambiar implica reiniciar sesión).
  const [pendingCompanyChange, setPendingCompanyChange] = useState<CompanyMaster | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [processed, setProcessed] = useState<ProcessedFile[]>([]);
  const [failedFiles, setFailedFiles] = useState<FailedFile[]>([]);
  const [trashedFiles, setTrashedFiles] = useState<TrashedFile[]>([]);
  const [extracting, setExtracting] = useState(0);
  const isProcessing = extracting > 0;
  useKeepAwake(isProcessing);

  // Refresco proactivo de la sesión mientras se procesa un lote: los tokens
  // JWT caducan a los 60 min y en lotes de >80 archivos podemos alcanzar ese
  // límite → 401. Refrescamos cada 40 min si seguimos procesando.
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => {
      supabase.auth.refreshSession().catch(() => {});
    }, 40 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isProcessing]);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [prefix, setPrefix] = useState("");
  const [startNumber, setStartNumber] = useState(1);
  const { toast } = useToast();
  const supplierMasterRef = useRef<Map<string, SupplierMasterEntry> | null>(null);
  // NIF de la empresa receptora ACTIVA (la seleccionada en el selector de
  // cliente). Se envía al extractor para que jamás lo devuelva como
  // supplierTaxId. Se actualiza cada vez que cambia `appliedCompany`.
  // Nota: usamos solo el NIF activo (no una blacklist global) para soportar
  // el caso de clientes del grupo que se facturan entre sí.
  const activeReceiverTaxIdRef = useRef<string>("");
  // Enfriamiento global ante 429: marca un timestamp futuro hasta el cual
  // los workers reducen su concurrencia para no machacar el gateway IA.
  const rateLimitCooldownUntilRef = useRef<number>(0);
  const [appliedCompany, setAppliedCompany] = useState<CompanyMaster | null>(null);
  // Referencia siempre actualizada de la empresa aplicada, para que las
  // llamadas a IA registren el consumo con la empresa correcta aunque el
  // callback se haya creado antes de aplicarla.
  const appliedCompanyRef = useRef<CompanyMaster | null>(null);
  appliedCompanyRef.current = appliedCompany;
  const [failedViewerFile, setFailedViewerFile] = useState<File | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<DocStatusFilterValue>("all");

  const [splitViewFailedId, setSplitViewFailedId] = useState<string | null>(null);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  const lastBatchSizeRef = useRef(0);
  const wasProcessingRef = useRef(false);
  const [isHydrated, setIsHydrated] = useState(false);
  // Bulk selection (file IDs of processed invoices selected for bulk actions)
  const [selectedProcessedIds, setSelectedProcessedIds] = useState<Set<string>>(new Set());
  // Selección de documentos NO procesados, para poder unirlos con facturas procesadas
  const [selectedFailedIds, setSelectedFailedIds] = useState<Set<string>>(new Set());

  // Mantener sincronizado el NIF receptor activo con la empresa aplicada.
  useEffect(() => {
    const raw = (appliedCompany?.tax_id || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    activeReceiverTaxIdRef.current = raw.length >= 8 ? raw : "";
  }, [appliedCompany]);

  // Cuentas de pago (caja/tarjetas) configuradas para la empresa aplicada.
  // Se usan para sugerir automáticamente el asiento de pago cuando la forma
  // de pago de la factura es CONTADO o TARJETA.
  const [paymentAccounts, setPaymentAccounts] = useState<CompanyPaymentAccounts | null>(null);
  const paymentAccountsRef = useRef<CompanyPaymentAccounts | null>(null);
  useEffect(() => {
    paymentAccountsRef.current = paymentAccounts;
  }, [paymentAccounts]);
  useEffect(() => {
    if (!appliedCompany?.id) {
      setPaymentAccounts(null);
      return;
    }
    let cancelled = false;
    loadCompanyPaymentAccounts(appliedCompany.id).then((acc) => {
      if (!cancelled) setPaymentAccounts(acc);
    });
    return () => {
      cancelled = true;
    };
  }, [appliedCompany?.id]);

  // Hydrate state from persisted session ONCE — never overwrite live state
  // if `initialState` is re-emitted later (e.g. auth re-renders).
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (initialState && !hydratedRef.current) {
      hydratedRef.current = true;
      setPendingFiles(initialState.pendingFiles);
      setProcessed(sanitizeProcessedCollection(initialState.processed));
      setFailedFiles(initialState.failedFiles);
      setTrashedFiles(initialState.trashedFiles || []);
      setPrefix((initialState.prefix || "").toUpperCase());
      setStartNumber(initialState.startNumber);
      setIsHydrated(true);
      // Rehidratar el cliente activo desde el id guardado (si existía).
      const savedCompanyId = initialState.appliedCompanyId;
      if (savedCompanyId) {
        (async () => {
          try {
            const all = await loadCompanyMasters();
            const found = all.find((c) => c.id === savedCompanyId);
            if (found) await handleApplyMaster(found);
          } catch (e) {
            console.error("No se pudo rehidratar la empresa activa:", e);
          }
        })();
      }
    }
  }, [initialState]);

  // Corrections map: composite key "CIF|SUPPLIER" → { correctName, correctTaxId }
  // Lookup uses fuzzy matching on supplier name to handle AI name variations
  const supplierCorrectionsRef = useRef<Map<string, { correctName: string; correctTaxId: string | null }>>(new Map());

  // Reverse index: correct_tax_id → { correctName, correctTaxId } for allTaxIds lookup
  const correctTaxIdIndexRef = useRef<Map<string, { correctName: string; correctTaxId: string }>>(new Map());

  // Simple string similarity (Dice coefficient on bigrams)
  const stringSimilarity = (a: string, b: string): number => {
    if (a === b) return 1;
    if (a.length < 2 || b.length < 2) return 0;
    const bigrams = (s: string) => {
      const set = new Map<string, number>();
      for (let i = 0; i < s.length - 1; i++) {
        const bg = s.substring(i, i + 2);
        set.set(bg, (set.get(bg) || 0) + 1);
      }
      return set;
    };
    const aBigrams = bigrams(a);
    const bBigrams = bigrams(b);
    let intersection = 0;
    for (const [bg, count] of aBigrams) {
      intersection += Math.min(count, bBigrams.get(bg) || 0);
    }
    return (2 * intersection) / (a.length - 1 + b.length - 1);
  };

  // Find best correction using ALL tax IDs found in the document
  // Priority: 1) Match correct_tax_id against allTaxIds  2) Fuzzy match on CIF+name
  const findCorrectionByAllTaxIds = (allTaxIds: string[], aiTaxId: string, aiSupplier: string) => {
    // Strategy 1: Check if any of the document's tax IDs matches a known correct_tax_id
    // This handles the case where AI picks the receiver's CIF but the real supplier's CIF is also in the doc
    if (allTaxIds && allTaxIds.length > 0) {
      for (const tid of allTaxIds) {
        const normTid = tid.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
        if (!normTid || normTid === aiTaxId) continue; // Skip the one AI already picked
        const match = correctTaxIdIndexRef.current.get(normTid);
        if (match) return match;
      }
    }

    // Strategy 2: Fallback to composite key fuzzy matching (original behavior)
    if (!aiTaxId) return undefined;
    const normSupplier = aiSupplier.trim().toUpperCase();
    const exactKey = `${aiTaxId}|${normSupplier}`;
    const exact = supplierCorrectionsRef.current.get(exactKey);
    if (exact) return exact;

    let bestMatch: { correctName: string; correctTaxId: string | null } | undefined;
    let bestScore = 0;
    const keyPrefix = `${aiTaxId}|`;
    for (const [key, value] of supplierCorrectionsRef.current) {
      if (key.startsWith(keyPrefix)) {
        const storedSupplier = key.substring(keyPrefix.length);
        const score = stringSimilarity(normSupplier, storedSupplier);
        if (score > bestScore && score > 0.4) {
          bestScore = score;
          bestMatch = value;
        }
      }
    }
    return bestMatch;
  };

  // Load supplier corrections from DB on mount

  useEffect(() => {
    const loadCorrections = async () => {
      try {
        const { data } = await supabase
          .from("supplier_corrections")
          .select("tax_id, original_supplier_name, correct_name, correct_tax_id");
        if (data) {
          const corrMap = new Map<string, { correctName: string; correctTaxId: string | null }>();
          const reverseMap = new Map<string, { correctName: string; correctTaxId: string }>();
          for (const row of data) {
            const normId = row.tax_id.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
            const normSupplier = (row.original_supplier_name || "").trim().toUpperCase();
            const compositeKey = `${normId}|${normSupplier}`;
            const correctTaxId = (row as any).correct_tax_id || null;
            corrMap.set(compositeKey, {
              correctName: row.correct_name,
              correctTaxId,
            });
            if (correctTaxId) {
              reverseMap.set(correctTaxId, { correctName: row.correct_name, correctTaxId });
            }
          }
          supplierCorrectionsRef.current = corrMap;
          correctTaxIdIndexRef.current = reverseMap;
        }
      } catch (e) {
        console.error("Failed to load supplier corrections:", e);
      }
    };
    loadCorrections();
  }, []);


  // Auto-save session to Supabase on state changes (only after hydration to avoid overwriting with empty state).
  // During IA extraction we deliberately pause persistence: uploading the same PDFs to storage while pdfjs
  // is rendering them and the IA requests are in flight creates unnecessary browser pressure in production.
  useEffect(() => {
    if (!currentUserId || sessionLoading || !isHydrated || isProcessing) return;
    saveSession({ pendingFiles, processed, failedFiles, trashedFiles, prefix, startNumber, appliedCompanyId: appliedCompany?.id ?? null });
  }, [currentUserId, pendingFiles, processed, failedFiles, trashedFiles, prefix, startNumber, saveSession, sessionLoading, isHydrated, isProcessing, appliedCompany?.id]);

  // Use refs so processFile always reads the latest values
  const prefixRef = useRef(prefix);
  prefixRef.current = prefix;
  const startNumberRef = useRef(startNumber);
  startNumberRef.current = startNumber;
  const processedRef = useRef<ProcessedFile[]>([]);
  processedRef.current = processed;
  const namingModeRef = useRef<"standard" | "original_numbered">("standard");
  namingModeRef.current = appliedCompany?.naming_mode ?? "standard";

  const recalculateNames = useCallback((files: ProcessedFile[], pfx: string, start: number): ProcessedFile[] => {
    const sorted = sortByDateThenNumber(files);
    const mode = namingModeRef.current;
    const seriesNumbers = computeSeriesNumbers(sorted, pfx, start);
    return sorted.map((f, i) => {
      const originalName = toSafeText(f.originalName) || "factura.pdf";
      const ext = originalName.split(".").pop()?.toLowerCase() || "pdf";
      const paymentSuffix = f.paymentMethod ? `_${toSafeText(f.paymentMethod)}` : "";
      const normalizedPlate = normalizeLicensePlate(f.licensePlate);
      const rebuiltBaseName = buildBaseName(
        toSafeText(f.date).replace(/-/g, ""),
        toSafeText(f.supplier),
        normalizeInvoiceNumber(f.invoiceNumber),
        paymentSuffix,
        ext,
        normalizedPlate,
      );
      const vatNumber = seriesNumbers.get(f.id) ?? `${(pfx || "").toUpperCase()}${start + i}`;
      let newName: string;
      if (mode === "original_numbered") {
        // Nº de IVA (sin ceros) + nombre original tal cual lo subió el usuario.
        const lastDot = originalName.lastIndexOf(".");
        const stem = lastDot > 0 ? originalName.slice(0, lastDot) : originalName;
        const realExt = lastDot > 0 ? originalName.slice(lastDot + 1) : ext;
        const cleanStem = stem.replace(/[<>:"/\\|?*\x00-\x1F]/g, "_").replace(/\.+$/, "").trim() || "archivo";
        const prefixPart = `${vatNumber}_`;
        const extPart = realExt ? `.${realExt}` : "";
        const available = MAX_BASENAME_LEN - prefixPart.length - extPart.length;
        const finalStem = available > 0 && cleanStem.length > available ? cleanStem.slice(0, available) : cleanStem;
        newName = `${prefixPart}${finalStem}${extPart}`;
      } else {
        newName = `${vatNumber}. ${rebuiltBaseName}`;
      }
      const nextInvoiceNumber = normalizeInvoiceNumber(f.invoiceNumber);
      if (f.newName === newName && f.baseName === rebuiltBaseName && f.invoiceNumber === nextInvoiceNumber) return f;
      return { ...f, licensePlate: normalizedPlate, invoiceNumber: nextInvoiceNumber, baseName: rebuiltBaseName, newName };
    });
  }, []);

  useEffect(() => {
    setProcessed((prev) => recalculateNames(sanitizeProcessedCollection(prev), prefixRef.current, startNumberRef.current));
    setFailedFiles((prev) => sanitizeFailedCollection(prev));
  }, [recalculateNames, appliedCompany?.naming_mode]);

  // Series adicionales definidas en «Numeración» (se guardan en este navegador por empresa).
  const seriesStorageKey = `fp_extra_series_${appliedCompany?.id ?? "none"}`;
  const [extraSeries, setExtraSeries] = useState<{ prefix: string; start: number }[]>([]);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(seriesStorageKey);
      setExtraSeries(raw ? JSON.parse(raw) : []);
    } catch { setExtraSeries([]); }
  }, [seriesStorageKey]);
  const persistExtraSeries = useCallback((next: { prefix: string; start: number }[]) => {
    setExtraSeries(next);
    try { localStorage.setItem(seriesStorageKey, JSON.stringify(next)); } catch { /* ignore */ }
  }, [seriesStorageKey]);

  // Lista combinada: principal + definidas + las que ya traen las facturas.
  const allSeries = useMemo(() => {
    const fromFiles = listSeries(processed, prefix, startNumber);
    const out = [...fromFiles];
    for (const s of extraSeries) {
      const p = s.prefix.trim().toUpperCase();
      if (!p || out.some((o) => o.prefix === p)) continue;
      out.push({ prefix: p, start: s.start, count: 0, isMain: false });
    }
    return out;
  }, [processed, prefix, startNumber, extraSeries]);
  const seriesOptions = useMemo(() => allSeries.map((s) => ({ prefix: s.prefix, isMain: s.isMain })), [allSeries]);

  const handleChangeSeries = useCallback((fileId: string, sp: string) => {
    const target = sp.trim().toUpperCase();
    const def = extraSeries.find((s) => s.prefix.toUpperCase() === target)
      ?? allSeries.find((s) => s.prefix === target);
    setProcessed((prev) => recalculateNames(prev.map((f) => {
      if (f.id !== fileId) return f;
      return target && target !== prefixRef.current.toUpperCase()
        ? { ...f, seriesPrefix: target, seriesStart: Math.max(1, def?.start || 1) }
        : { ...f, seriesPrefix: undefined, seriesStart: undefined };
    }), prefixRef.current, startNumberRef.current));
  }, [extraSeries, allSeries, recalculateNames]);

  // Edita una serie adicional: actualiza la definición y las facturas que la usan.
  const handleEditExtraSeries = useCallback((index: number, patch: { prefix?: string; start?: number }) => {
    const list = allSeries.filter((s) => !s.isMain).map((s) => ({ prefix: s.prefix, start: s.start }));
    const old = list[index];
    if (!old) return;
    const next = { prefix: (patch.prefix ?? old.prefix).toUpperCase(), start: Math.max(1, patch.start ?? old.start) };
    list[index] = next;
    persistExtraSeries(list);
    setProcessed((prev) => recalculateNames(prev.map((f) =>
      (f.seriesPrefix ?? "").toUpperCase() === old.prefix && old.prefix
        ? { ...f, seriesPrefix: next.prefix || undefined, seriesStart: next.prefix ? next.start : undefined }
        : f), prefixRef.current, startNumberRef.current));
  }, [allSeries, persistExtraSeries, recalculateNames]);

  const handleRemoveExtraSeries = useCallback((index: number) => {
    const list = allSeries.filter((s) => !s.isMain).map((s) => ({ prefix: s.prefix, start: s.start }));
    const old = list[index];
    if (!old) return;
    persistExtraSeries(list.filter((_, i) => i !== index));
    setProcessed((prev) => recalculateNames(prev.map((f) =>
      (f.seriesPrefix ?? "").toUpperCase() === old.prefix
        ? { ...f, seriesPrefix: undefined, seriesStart: undefined }
        : f), prefixRef.current, startNumberRef.current));
  }, [allSeries, persistExtraSeries, recalculateNames]);

  const updateProcessedCoreFields = useCallback(
    (
      file: ProcessedFile,
      fields: Partial<Pick<ProcessedFile, "date" | "supplier" | "invoiceNumber" | "paymentMethod">>,
    ): ProcessedFile => {
      const nextDate = toSafeText(fields.date ?? file.date);
      const nextSupplier = toSafeText(fields.supplier ?? file.supplier);
      const nextInvoiceNumber = normalizeInvoiceNumber(fields.invoiceNumber ?? file.invoiceNumber);
      const nextPaymentMethod = fields.paymentMethod !== undefined ? fields.paymentMethod : file.paymentMethod;

      const formattedDate = nextDate.replace(/-/g, "");
      const paymentSuffix = nextPaymentMethod ? `_${nextPaymentMethod}` : "";
      const ext = toSafeText(file.originalName).split(".").pop()?.toLowerCase() || "pdf";
      const baseName = buildBaseName(formattedDate, nextSupplier, nextInvoiceNumber, paymentSuffix, ext, file.licensePlate);

      return {
        ...file,
        date: nextDate,
        supplier: nextSupplier,
        invoiceNumber: nextInvoiceNumber,
        paymentMethod: nextPaymentMethod,
        baseName,
      };
    },
    [],
  );

  const saveSupplierCorrection = useCallback(
    async (aiExtractedTaxId: string, aiExtractedSupplier: string, correctName: string, correctTaxId: string | null) => {
      if (!aiExtractedTaxId || !correctName) return;
      const normKey = aiExtractedTaxId.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
      const normSupplier = (aiExtractedSupplier || "").trim().toUpperCase();
      const compositeKey = `${normKey}|${normSupplier}`;
      const normCorrectTaxId = correctTaxId ? correctTaxId.replace(/[^a-zA-Z0-9]/g, "").toUpperCase() : null;

      supplierCorrectionsRef.current.set(compositeKey, { correctName, correctTaxId: normCorrectTaxId });

      if (normCorrectTaxId) {
        correctTaxIdIndexRef.current.set(normCorrectTaxId, { correctName, correctTaxId: normCorrectTaxId });
      }

      try {
        await supabase.from("supplier_corrections").upsert(
          {
            user_id: currentUserId,
            tax_id: normKey,
            original_supplier_name: normSupplier,
            correct_name: correctName,
            correct_tax_id: normCorrectTaxId,
            updated_at: new Date().toISOString(),
          } as any,
          { onConflict: "user_id,tax_id,original_supplier_name" },
        );
      } catch (e) {
        console.error("Failed to save supplier correction:", e);
      }
    },
    [],
  );

  /** Re-scan all processed files and refresh isDuplicate flags */
  const recheckAllDuplicates = useCallback((files: ProcessedFile[]): ProcessedFile[] => {
    const normalize = (s: string) => (s || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    // Reset all
    const updated = files.map((f) => ({ ...f, isDuplicate: false }));

    for (let i = 0; i < updated.length; i++) {
      // Check vs contabilizado entries
      const isDupContabilizado = checkDuplicate(
        updated[i].supplierTaxId || "",
        updated[i].invoiceNumber,
        updated[i].originalName,
        updated[i].supplier,
        currentUserId,
      );
      if (isDupContabilizado) {
        updated[i].isDuplicate = true;
      }

      // Check vs other processed files by original filename without mutating React state in place
      if (!updated[i].isDuplicate) {
        const originalName = toSafeText(updated[i].originalName);
        if (originalName) {
          for (let j = 0; j < updated.length; j++) {
            if (i === j) continue;
            if (originalName === toSafeText(updated[j].originalName)) {
              updated[i].isDuplicate = true;
              updated[j].isDuplicate = true;
              break;
            }
          }
        }
      }

      // Check vs other processed files
      if (!updated[i].isDuplicate) {
        const normTaxId = normalize(updated[i].supplierTaxId || "");
        const normInvNum = normalize(updated[i].invoiceNumber);
        for (let j = 0; j < updated.length; j++) {
          if (i === j) continue;
          const exTaxId = normalize(updated[j].supplierTaxId || "");
          const exInvNum = normalize(updated[j].invoiceNumber);
          if (normTaxId && normInvNum && exTaxId === normTaxId && exInvNum === normInvNum) {
            updated[i].isDuplicate = true;
            updated[j].isDuplicate = true;
            break;
          }
        }
      }
    }
    return updated;
  }, [currentUserId]);

  // ── Cotejo contra histórico permanente (Entrega 5) ──
  useEffect(() => {
    const companyId = appliedCompany?.id;
    if (!companyId || processed.length === 0) {
      if (processed.some((p) => p.historyMatch)) {
        setProcessed((prev) => prev.map((p) => ({ ...p, historyMatch: null })));
      }
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const nifs = Array.from(new Set(
          processed.flatMap((p) => [(p?.supplierTaxId || "").trim(), (p?.originalExtractedTaxId || "").trim()]).filter(Boolean),
        ));
        if (nifs.length === 0) return;
        const cache = new Map<string, InvoiceHistoryEntry[]>();
        for (const nif of nifs) {
          try {
            const rows = await loadHistoryByNif(companyId, nif);
            if (cancelled) return;
            cache.set(nif.replace(/[^a-zA-Z0-9]/g, "").toUpperCase(), rows);
          } catch (err) {
            console.error("[history] loadHistoryByNif failed for", nif, err);
            cache.set(nif.replace(/[^a-zA-Z0-9]/g, "").toUpperCase(), []);
          }
        }
        if (cancelled) return;
        setProcessed((prev) => prev.map((p) => {
          try {
            const nifNorm = (p?.supplierTaxId || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
            const rows = cache.get(nifNorm) ?? [];
            const breakdown = Array.isArray(p?.vatBreakdown) ? p.vatBreakdown : [];
            const baseSum = breakdown.reduce(
              (acc, l) => acc + (l && Number.isFinite(Number(l.taxableBase)) ? Number(l.taxableBase) : 0),
              0,
            );
            const totalNum = Number(p?.totalAmount ?? 0);
            const extNorm = (p?.originalExtractedTaxId || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
            const rowsAll = extNorm && extNorm !== nifNorm ? [...rows, ...(cache.get(extNorm) ?? [])] : rows;
            const m = matchAgainstHistory({
              supplier_tax_id: p?.supplierTaxId || "",
              extracted_tax_id: p?.originalExtractedTaxId || null,
              invoice_number: p?.invoiceNumber || null,
              invoice_date: p?.date || null,
              base_amount: baseSum > 0 ? baseSum : null,
              total_amount: Number.isFinite(totalNum) ? totalNum : 0,
            }, rowsAll);
            return { ...p, historyMatch: m ? { level: m.level, reason: m.reason } : null };
          } catch (err) {
            console.error("[history] match failed for file", p?.id, err);
            return p;
          }
        }));
      } catch (err) {
        console.error("[history] effect failed:", err);
      }
    };
    void run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCompany?.id, processed.length, processed.map((p) => `${p?.id}:${p?.supplierTaxId}:${p?.invoiceNumber}:${p?.date}:${p?.totalAmount}:${(Array.isArray(p?.vatBreakdown) ? p.vatBreakdown : []).reduce((a, l) => a + (Number(l?.taxableBase) || 0), 0)}`).join("|")]);

  // ── Telemetría: registra processed/failed cuando termina un lote ──
  useEffect(() => {
    if (isProcessing) {
      wasProcessingRef.current = true;
      lastBatchSizeRef.current = Math.max(lastBatchSizeRef.current, totalToProcess);
    } else if (wasProcessingRef.current && !isProcessing) {
      wasProcessingRef.current = false;
      const batchSize = lastBatchSizeRef.current;
      lastBatchSizeRef.current = 0;
      const rejectedNow = failedFiles.filter((f) => f.kind === "rejected").length;
      const errorNow = failedFiles.filter((f) => f.kind !== "rejected").length;
      const failedNow = rejectedNow + errorNow;
      const processedNow = Math.max(0, batchSize - failedNow);
      // Los documentos procesados y descartados los registra el servidor al leer
      // cada factura, para no perder eventos en lotes grandes.
      if (currentUserId && errorNow > 0) {
        void recordProcessingEvent({ userId: currentUserId, eventType: "failed", count: errorNow, companyId: appliedCompany?.id ?? null });
      }
      if (batchSize >= 5) {
        browserNotify("Procesamiento finalizado", {
          body: `${processedNow} procesadas · ${errorNow} fallidas${rejectedNow > 0 ? ` · ${rejectedNow} descartadas` : ""}`,
          tag: "batch-done",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);

  // ── Auto-aplicar maestro/leasing/historial ──
  // Se dispara (a) al terminar un lote y (b) cuando el usuario corrige un NIF
  // o matrícula durante la revisión, para que el enlace al maestro de
  // proveedores, leasing y contrapartidas se actualice sin pasos manuales.
  const lastEnrichedSigRef = useRef<string>("");
  useEffect(() => {
    if (isProcessing) return;
    if (!appliedCompany) return;
    if (processed.length === 0) return;
    // La firma incluye NIF, matrícula, nº de contrato, fecha y base de cada factura: si el
    // usuario edita cualquiera de ellos, el efecto vuelve a aplicar el maestro
    // solo para enriquecer la(s) factura(s) afectada(s) sin pisar otras ediciones.
    const sig = `${appliedCompany.id}::${processed
      .map((p) => {
        const baseSum = (Array.isArray(p.vatBreakdown) ? p.vatBreakdown : [])
          .reduce((sum, line) => sum + toSafeNumber(line?.taxableBase), 0);
        return `${p.id}|${(p.supplierTaxId || "").toUpperCase()}|${(p.licensePlate || "").toUpperCase()}|${(p.contractNumber || "").toUpperCase()}|${p.date || ""}|${roundCurrency(baseSum)}`;
      })
      .join("§")}`;
    if (sig === lastEnrichedSigRef.current) return;
    lastEnrichedSigRef.current = sig;
    void handleApplyMaster(appliedCompany);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing, appliedCompany?.id, processed]);

  // ── Atajos: Ctrl+K abre buscador, Esc lo cierra ──
  useKeyboardShortcuts([
    { key: "k", ctrl: true, handler: () => setGlobalSearchOpen(true) },
    { key: "Escape", handler: () => setGlobalSearchOpen(false), allowInInputs: true, preventDefault: false },
  ]);

  // ── Pedir permiso de notificaciones cuando se inicia un lote >= 10 ──
  useEffect(() => {
    if (totalToProcess >= 10) {
      void requestNotifPermission();
    }
  }, [totalToProcess]);



  const handleUpdateFile = useCallback(
    (id: string, fields: { date: string; supplier: string; invoiceNumber: string; supplierTaxId?: string }) => {
      setProcessed((prev) => {
        const file = prev.find((f) => f.id === id);
        if (!file) return prev;

        const newTaxId = fields.supplierTaxId;
        const nameChanged = fields.supplier && toSafeText(fields.supplier) !== toSafeText(file.supplier);
        const cifChanged =
          newTaxId &&
          file.supplierTaxId &&
          toSafeText(newTaxId).replace(/[^a-zA-Z0-9]/g, "").toUpperCase() !==
            toSafeText(file.supplierTaxId).replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

        if (nameChanged || cifChanged) {
          const correctionKey = file.originalExtractedTaxId || file.supplierTaxId || newTaxId || "";
          const originalSupplier = file.originalExtractedSupplier || file.supplier || "";
          const saveName = toSafeText(fields.supplier) || toSafeText(file.supplier);
          const saveTaxId = cifChanged ? newTaxId! : file.supplierTaxId !== correctionKey ? file.supplierTaxId! : null;
          saveSupplierCorrection(correctionKey, originalSupplier, saveName, saveTaxId);
        }

        const updated = prev.map((f) => {
          if (f.id !== id) return f;
          const base = updateProcessedCoreFields(f, fields);
          return {
            ...base,
            ...(fields.supplierTaxId ? { supplierTaxId: fields.supplierTaxId } : {}),
            // Si cambió el proveedor o su NIF, permitir una nueva consulta de contrapartida.
            ...(nameChanged || cifChanged ? { aiSuggestionAttempted: false } : {}),
            // El usuario revisó/editó: limpiamos el aviso de confusión emisor/receptor.
            receiverConfusion: false,
            // El usuario ha pulsado Guardar → la factura queda validada.
            pendingValidation: false,
          };
        });
        return recheckAllDuplicates(recalculateNames(updated, prefixRef.current, startNumberRef.current));
      });
    },
    [recalculateNames, saveSupplierCorrection, updateProcessedCoreFields, recheckAllDuplicates],
  );

  const handleDeleteProcessed = useCallback(
    (id: string) => {
      setProcessed((prev) => {
        const file = prev.find((f) => f.id === id);
        const filtered = prev.filter((f) => f.id !== id);
        if (file) {
          setTrashedFiles((t) => [...t, { file, origin: "processed" }]);
        }
        const rechecked = recheckAllDuplicates(filtered);
        return recalculateNames(rechecked, prefixRef.current, startNumberRef.current);
      });
      setSelectedProcessedIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [recalculateNames, recheckAllDuplicates],
  );

  const handleRestoreProcessed = useCallback(
    (file: ProcessedFile) => {
      setTrashedFiles((prev) => prev.filter((t) => t.file.id !== file.id));
      setProcessed((prev) => {
        const next = [...prev, file];
        const rechecked = recheckAllDuplicates(next);
        return recalculateNames(rechecked, prefixRef.current, startNumberRef.current);
      });
    },
    [recalculateNames, recheckAllDuplicates],
  );

  const handleDeleteFailed = useCallback(
    (id: string) => {
      setFailedFiles((prev) => {
        const file = prev.find((f) => f.id === id);
        const filtered = prev.filter((f) => f.id !== id);
        if (file) {
          setTrashedFiles((t) => [...t, { file, origin: "failed" }]);
        }
        return filtered;
      });
    },
    [],
  );

  const handleRestoreFailed = useCallback(
    (file: FailedFile) => {
      setTrashedFiles((prev) => prev.filter((t) => t.file.id !== file.id));
      setFailedFiles((prev) => [...prev, file]);
    },
    [],
  );

  const handlePermanentDelete = useCallback(
    (id: string) => {
      setTrashedFiles((prev) => prev.filter((t) => t.file.id !== id));
    },
    [],
  );

  const handleEmptyTrash = useCallback(() => {
    setTrashedFiles([]);
  }, []);
  const [splitterFile, setSplitterFile] = useState<{ id: string; file: File } | null>(null);

  const handleOpenSplitter = useCallback((pf: ProcessedFile) => {
    setOpenInvoiceId(null); // cierra el visor detallado antes de abrir el separador
    setSplitterFile({ id: pf.id, file: pf.file });
  }, []);

  const handleSplitConfirm = useCallback(
    (parts: File[]) => {
      if (!splitterFile) return;
      const originalId = splitterFile.id;
      // Quita el documento original de "procesadas" y recalcula IVA/nombres.
      setProcessed((prev) => {
        const filtered = prev.filter((f) => f.id !== originalId);
        const rechecked = recheckAllDuplicates(filtered);
        return recalculateNames(rechecked, prefixRef.current, startNumberRef.current);
      });
      // Inserta las partes como pendientes para volver a extraer con IA.
      const batchId = `${Date.now()}:split:${Math.random().toString(36).slice(2, 6)}`;
      const newPending = parts.map((file, index) => ({
        id: createStableId(getFileStableSeed(file, "pending", `${batchId}:${index}`)),
        file,
      }));
      setPendingFiles((prev) => [...prev, ...newPending]);
      setSplitterFile(null);
      toast({
        title: `Documento separado en ${parts.length} archivos`,
        description: "Pulsa «Procesar con IA» para reconocer cada parte. Se renumerará el nº de IVA.",
      });
    },
    [splitterFile, recalculateNames, recheckAllDuplicates, toast],
  );


  // ─── Bulk selection ───────────────────────────────────
  const handleToggleSelectProcessed = useCallback((id: string) => {
    setSelectedProcessedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedProcessedIds(new Set());
    setSelectedFailedIds(new Set());
  }, []);

  const handleToggleFailedSelection = useCallback((id: string) => {
    setSelectedFailedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleSelectAllProcessed = useCallback(() => {
    setSelectedProcessedIds((prev) => {
      // Use the latest processed list at apply time (ref-based via setProcessed callback wouldn't help here)
      // We rely on the closure of `processed` via a ref-like read.
      // Simple approach: if any selection exists → clear; otherwise select all currently in `processed`.
      if (prev.size > 0) return new Set();
      return new Set(processedRef.current.map((f) => f.id));
    });
  }, []);

  const handleBulkDelete = useCallback(() => {
    let deletedCount = 0;
    setProcessed((prev) => {
      const toTrash = prev.filter((f) => selectedProcessedIds.has(f.id));
      deletedCount = toTrash.length;
      const filtered = prev.filter((f) => !selectedProcessedIds.has(f.id));
      setTrashedFiles((t) => [...t, ...toTrash.map((file) => ({ file, origin: "processed" as const }))]);
      const rechecked = recheckAllDuplicates(filtered);
      return recalculateNames(rechecked, prefixRef.current, startNumberRef.current);
    });
    if (selectedFailedIds.size > 0) {
      setFailedFiles((prev) => {
        const toTrash = prev.filter((f) => selectedFailedIds.has(f.id));
        deletedCount += toTrash.length;
        setTrashedFiles((t) => [...t, ...toTrash.map((file) => ({ file, origin: "failed" as const }))]);
        return prev.filter((f) => !selectedFailedIds.has(f.id));
      });
    }
    setSelectedProcessedIds(new Set());
    setSelectedFailedIds(new Set());
    toast({ title: "Documentos desechados", description: `Se desecharon ${deletedCount} documentos.` });
  }, [recalculateNames, recheckAllDuplicates, selectedProcessedIds, selectedFailedIds, toast]);

  const [mergeCandidates, setMergeCandidates] = useState<File[] | null>(null);

  const handleBulkMerge = useCallback(() => {
    const ids = selectedProcessedIds;
    const failedIds = selectedFailedIds;
    if (ids.size + failedIds.size < 2) return;
    // Recolectar en el orden actual de las listas: primero procesadas, después no procesadas
    const selected = processedRef.current.filter((f) => ids.has(f.id));
    const selectedFailed = failedFiles.filter((f) => failedIds.has(f.id));
    const filesToMerge = [
      ...selected.map((p) => p.file),
      ...selectedFailed.map((f) => f.file),
    ].filter(Boolean) as File[];
    if (filesToMerge.length < 2) {
      toast({ title: "No se puede unir", description: "Faltan archivos originales.", variant: "destructive" });
      return;
    }
    setMergeCandidates(filesToMerge);
  }, [selectedProcessedIds, selectedFailedIds, failedFiles, toast]);

  const [mergeTargetId, setMergeTargetId] = useState<string | null>(null);
  const handleMergeWith = useCallback(async (pf: ProcessedFile, extra: File[], mode: "reprocess" | "annex" = "reprocess") => {
    if (!pf.file) return;
    if (mode === "annex") {
      try {
        const merged = await mergeFilesToPdf([pf.file, ...extra]);
        const file = new File([merged], pf.file.name.replace(/\.[^.]+$/, "") + ".pdf", { type: "application/pdf" });
        setProcessed((prev) => prev.map((f) => (f.id === pf.id ? { ...f, file } : f)));
        toast({ title: "Anexo añadido", description: `${extra.length} archivo(s) añadidos al final del PDF. Los datos de la factura no cambian.` });
      } catch (e) {
        toast({ title: "Error al anexar", description: e instanceof Error ? e.message : "No se pudo añadir el anexo.", variant: "destructive" });
      }
      return;
    }
    setOpenInvoiceId(null);
    setMergeTargetId(pf.id);
    setMergeCandidates([pf.file, ...extra]);
  }, [toast]);

  const handleConfirmMerge = useCallback(async (filesToMerge: File[]) => {
    const ids = mergeTargetId ? new Set([mergeTargetId]) : selectedProcessedIds;
    const failedIds = mergeTargetId ? new Set<string>() : selectedFailedIds;
    setMergeTargetId(null);
    setMergeCandidates(null);
    try {
      const merged = await mergeFilesToPdf(filesToMerge);
      // Mover originales a papelera y recalcular
      setProcessed((prev) => {
        const toTrash = prev.filter((f) => ids.has(f.id));
        const filtered = prev.filter((f) => !ids.has(f.id));
        setTrashedFiles((t) => [...t, ...toTrash.map((file) => ({ file, origin: "processed" as const }))]);
        const rechecked = recheckAllDuplicates(filtered);
        return recalculateNames(rechecked, prefixRef.current, startNumberRef.current);
      });
      if (failedIds.size > 0) {
        setFailedFiles((prev) => {
          const toTrash = prev.filter((f) => failedIds.has(f.id));
          setTrashedFiles((t) => [...t, ...toTrash.map((file) => ({ file, origin: "failed" as const }))]);
          return prev.filter((f) => !failedIds.has(f.id));
        });
      }
      setSelectedProcessedIds(new Set());
      setSelectedFailedIds(new Set());
      // Añadir el PDF unido a pendientes para reprocesarlo con el pipeline existente
      await handleFilesSelect([merged]);
      toast({ title: "Documentos unidos", description: `Se combinaron ${filesToMerge.length} archivos. Pulsa «Procesar con IA» para reprocesar.` });
    } catch (e) {
      console.error("Merge error:", e);
      toast({
        title: "Error al unir",
        description: e instanceof Error ? e.message : "No se pudieron unir los archivos.",
        variant: "destructive",
      });
    }
  }, [recalculateNames, recheckAllDuplicates, selectedProcessedIds, selectedFailedIds, failedFiles, toast, mergeTargetId]);

  const handleBulkApplyPatch = useCallback(
    (patch: BulkPatch) => {
      const ids = selectedProcessedIds;
      if (ids.size === 0) return;
      setProcessed((prev) => {
        const mapped = prev.map((f) => {
          if (!ids.has(f.id)) return f;
          let next: ProcessedFile = f;
          if (typeof patch.seriesPrefix === "string") {
            const sp = patch.seriesPrefix.trim().toUpperCase();
            next = sp && sp !== prefixRef.current.toUpperCase()
              ? { ...next, seriesPrefix: sp, seriesStart: Math.max(1, patch.seriesStart || 1) }
              : { ...next, seriesPrefix: undefined, seriesStart: undefined };
          }
          if (typeof patch.expenseAccount === "string") {
            const acc = patch.expenseAccount;
            const breakdown = (f.vatBreakdown || [{ taxableBase: 0, vatRate: 0, vatAmount: 0 }]).map((line) => ({
              ...line,
              expenseAccount: acc,
            }));
            next = { ...next, expenseAccountCode: acc, vatBreakdown: breakdown };
          }
          if (typeof patch.irpfRate === "number" && Number.isFinite(patch.irpfRate)) {
            const rate = patch.irpfRate;
            const lines = next.vatBreakdown || [];
            const baseSum = lines.reduce((s, l) => s + toSafeNumber(l.taxableBase), 0);
            const vatSum = lines.reduce((s, l) => s + toSafeNumber(l.vatAmount), 0);
            // IRPF sólo sobre bases sujetas a IVA (excluye suplidos / exentos con % IVA = 0).
            const irpfBaseSum = computeIrpfTaxableBase(lines, baseSum);
            const irpfAmount = roundCurrency(irpfBaseSum * (rate / 100));
            const total = toSafeNumber(next.totalAmount);
            const calc = roundCurrency(baseSum + vatSum - irpfAmount);
            const inconsistent = total > 0 && Math.abs(total - calc) > 0.05;
            next = {
              ...next,
              irpfRate: rate,
              irpfAmount,
              calculatedTotal: calc,
              totalInconsistency: inconsistent,
            };
          }
          return next;
        });
        return typeof patch.seriesPrefix === "string"
          ? recalculateNames(mapped, prefixRef.current, startNumberRef.current)
          : mapped;
      });
      const labels: string[] = [];
      if (patch.expenseAccount) labels.push(`cuenta ${patch.expenseAccount}`);
      if (typeof patch.irpfRate === "number") labels.push(`IRPF ${patch.irpfRate}%`);
      if (typeof patch.seriesPrefix === "string") {
        labels.push(patch.seriesPrefix.trim() ? `serie ${patch.seriesPrefix.trim().toUpperCase()}` : "serie principal");
      }
      toast({
        title: "Cambios aplicados",
        description: `${ids.size} facturas · ${labels.join(", ")}`,
      });
    },
    [selectedProcessedIds, toast],
  );

  const handleApplyMaster = useCallback(async (company: CompanyMaster) => {
    const master = await loadSupplierMaster(company.id);
    supplierMasterRef.current = master;
    setAppliedCompany(company);

    // Load leasing data and contrapartidas catalog for the company
    const [leasingData, catalog] = await Promise.all([
      loadLeasingData(company.id),
      loadCompanyAccountCatalog(company.id),
    ]);

    // Helpers for plate/contract → catalog matching
    const normalizePlateLocal = (p?: string) => (p || "").replace(/[\s\-]/g, "").toUpperCase();
    const normalizeText = (t?: string) => (t || "").replace(/[\s\-_.]/g, "").toUpperCase();

    const findCatalogMatchByPlateOrContract = (plate?: string, contract?: string) => {
      const normPlate = normalizePlateLocal(plate);
      const normContract = normalizeText(contract);
      if (!normPlate && !normContract) return null;
      for (const c of catalog) {
        if (c.account.trim().startsWith("524")) continue;
        const desc = normalizeText(c.description);
        if (!desc) continue;
        if (normPlate && desc.includes(normPlate)) return c;
        if (normContract && desc.includes(normContract)) return c;
      }
      return null;
    };

    // Load supplier_account_history for this company (current user)
    let historyByTaxId = new Map<string, { account: string; description: string; useCount: number }[]>();
    try {
      const { loadAccountHistory } = await import("@/lib/accountHistory");
      historyByTaxId = await loadAccountHistory(company.id);
    } catch (e) {
      console.warn("[handleApplyMaster] history load failed:", e);
    }
    const { getDominantAccount } = await import("@/lib/accountHistory");
    const normalizeNifLocal = (nif?: string) => (nif || "").replace(/[\s.\-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");
    const catalogAccountSet = new Set(catalog.map((c) => c.account.trim()));
    const splitVatAmount = (base: number, totalBase: number, totalVat: number, rate: number) => {
      if (Math.abs(totalVat) > 0 && Math.abs(totalBase) > 0) {
        return roundCurrency((totalVat * base) / totalBase);
      }
      return roundCurrency((base * rate) / 100);
    };
    const invoiceTotalsFromBreakdown = (breakdown?: ProcessedFile["vatBreakdown"]) => {
      const lines = Array.isArray(breakdown) ? breakdown : [];
      const base = roundCurrency(lines.reduce((s, l) => s + toSafeNumber(l.taxableBase), 0));
      const vat = roundCurrency(lines.reduce((s, l) => s + toSafeNumber(l.vatAmount), 0));
      return { base, vat };
    };

    const findHistoryMatch = (taxId?: string) => {
      const key = normalizeNifLocal(taxId);
      if (!key) return null;
      const list = historyByTaxId.get(key);
      if (!list || list.length === 0) return null;
      for (const h of list) {
        if (h.account.trim().startsWith("524")) continue;
        if (catalogAccountSet.has(h.account.trim())) return h;
      }
      return null;
    };

    /**
     * Aggressive auto-assignment (Entrega 3.1):
     * If a supplier has ≥3 historical uses AND the top account represents >80%
     * of those uses AND it exists in the catalog, return it directly without
     * calling the AI.
     */
    const findDominantHistoryMatch = (taxId?: string) => {
      const key = normalizeNifLocal(taxId);
      if (!key) return null;
      const list = historyByTaxId.get(key);
      const dom = getDominantAccount(list);
      if (!dom) return null;
      if (dom.account.trim().startsWith("524")) return null;
      if (!catalogAccountSet.has(dom.account.trim())) return null;
      return dom;
    };

    // Track which files need AI suggestion (no leasing, no plate/contract match, no manual value)
    const needsSuggestion: { id: string; file: File }[] = [];

    setProcessed((prev) =>
      prev.map((f) => {
        const entry = f.supplierTaxId ? lookupSupplier(master, f.supplierTaxId, { licensePlate: f.licensePlate }) : undefined;

        // Priority 1: leasing → build vatBreakdown lines (524 / 6623 / 625), each with its expenseAccount
        let leasingFields: Partial<ProcessedFile> = {};
        let isLeasing = false;
        // Leasing only applies to specific supplier accounts (DL Iberica Equiprent, Banco Santander)
        const supplierAccountCode = entry?.accountCode?.trim() || "";
        const LEASING_SUPPLIER_ACCOUNTS = new Set(["41000939", "41000727", "41000149", "41000914"]);
        // Traton (Scania): no usa tabla de amortización; el desglose viene en la factura.
        const INVOICE_BREAKDOWN_ACCOUNTS = new Set(["41000727", "41000149"]);
        // El proveedor de la factura coincide en NIF con algún contrato de leasing cargado:
        // esto cubre proveedores Daimler / DL Ibérica / Santander / etc. aunque su cuenta 410xxx
        // varíe entre empresas y no esté en LEASING_SUPPLIER_ACCOUNTS.
        const normNif = (s: string | undefined) => (s || "").replace(/[\s.\-]/g, "").toUpperCase();
        const invoiceNifNorm = normNif(f.supplierTaxId);
        const supplierIsLeasingByNif = !!(
          leasingData && invoiceNifNorm &&
          leasingData.contracts.some((c) => normNif(c.supplier_tax_id) === invoiceNifNorm)
        );
        const invoiceBaseAmount = invoiceTotalsFromBreakdown(f.vatBreakdown).base;

        // Try to match against the leasing schedule. Primary rule: if the invoice
        // has a contract number, relate it to its amortization table by contract.
        // Plate is only a fallback for known leasing suppliers when the contract is absent.
        let leasingMatch: ReturnType<typeof matchLeasing> | null = null;
        if (leasingData) {
          if (f.contractNumber) {
            leasingMatch = supplierAccountCode === "41000149"
              ? null
              : matchLeasing(leasingData, f.contractNumber, f.licensePlate, f.date, f.supplierTaxId, invoiceBaseAmount);
          } else if (supplierIsLeasingByNif || LEASING_SUPPLIER_ACCOUNTS.has(supplierAccountCode)) {
            // Para Traton no intentamos matchear contra schedule (no existe).
            leasingMatch = supplierAccountCode === "41000149"
              ? null
              : matchLeasing(leasingData, f.contractNumber, f.licensePlate, f.date, f.supplierTaxId, invoiceBaseAmount);
          }
          // Diagnóstico: imprime por consola el resultado del matching para facturas potencialmente leasing.
          if (f.contractNumber || supplierIsLeasingByNif) {
            const normNifDbg = (s?: string) => (s || "").replace(/[\s.\-]/g, "").toUpperCase();
            console.log("[leasingMatch]", {
              file: f.originalName,
              nif: f.supplierTaxId,
              contract: f.contractNumber,
              plate: f.licensePlate,
              date: f.date,
              base: invoiceBaseAmount,
              supplierAccountCode,
              supplierIsLeasingByNif,
              contractsForNif: leasingData.contracts
                .filter((c) => normNifDbg(c.supplier_tax_id) === normNifDbg(f.supplierTaxId))
                .map((c) => `${c.contract_number} (${c.plate_number})`),
              matched: leasingMatch ? {
                contract: leasingMatch.contractNumber,
                installment: leasingMatch.installmentNumber,
                cost524: leasingMatch.costRecoveryAccount,
                int6623: leasingMatch.financialChargeAccount,
              } : "NO MATCH",
            });
          }
        }

        if (leasingMatch && !f.manualBreakdown) {
          const match = leasingMatch;
          isLeasing = true;

          // Tabla de leasing: la 524 toma la columna Amortización y la 6623 la columna Intereses.
          // Ambas líneas mantienen IVA 21% y se respeta la cuota IVA leída en la factura.
          const vatRate = 21;
          const amortizationBase = parseNumericLike(match.costRecovery);
          const interestBase = parseNumericLike(match.financialCharge);
          const ins = parseNumericLike(match.insurance);
          const originalTotals = invoiceTotalsFromBreakdown(f.vatBreakdown);
          const scheduleBase = roundCurrency(amortizationBase + interestBase + ins);
          const vatOnAmortization = splitVatAmount(amortizationBase, scheduleBase, originalTotals.vat, vatRate);
          const vatOnInterest = splitVatAmount(interestBase, scheduleBase, originalTotals.vat, vatRate);
          const vatOnInsurance = roundCurrency(originalTotals.vat - vatOnAmortization - vatOnInterest);

          const newBreakdown: NonNullable<ProcessedFile["vatBreakdown"]> = [
            {
              taxableBase: amortizationBase,
              vatRate,
              vatAmount: vatOnAmortization,
              expenseAccount: match.costRecoveryAccount,
            },
          ];
          if (interestBase !== 0) {
            newBreakdown.push({
              taxableBase: interestBase,
              vatRate,
              vatAmount: vatOnInterest,
              expenseAccount: match.financialChargeAccount,
            });
          }
          if (ins !== 0) {
            newBreakdown.push({
              taxableBase: ins,
              vatRate,
              vatAmount: vatOnInsurance,
              expenseAccount: match.insuranceAccount,
            });
          }
          const calculatedTotal = roundCurrency(scheduleBase + originalTotals.vat - toSafeNumber(f.irpfAmount));
          const invoiceTotal = toSafeNumber(f.totalAmount);
          const totalInconsistency = invoiceTotal !== 0 && Math.abs(invoiceTotal - calculatedTotal) > 0.05;

          leasingFields = {
            contractNumber: match.contractNumber,
            installmentNumber: match.installmentNumber,
            vatBreakdown: newBreakdown,
            expenseAccountCode: match.costRecoveryAccount,
            calculatedTotal,
            totalInconsistency,
            // Clear legacy leasing fields so the special columns disappear
            leasingCostRecovery: undefined,
            leasingFinancialCharge: undefined,
            leasingInsurance: undefined,
            leasingCostRecoveryAccount: undefined,
            leasingFinancialChargeAccount: undefined,
            leasingInsuranceAccount: undefined,
          };
        } else if (
          leasingData &&
          !f.manualBreakdown &&
          INVOICE_BREAKDOWN_ACCOUNTS.has(supplierAccountCode) &&
            f.leasingAmortization &&
            f.leasingInterest
          ) {
            // Santander / Traton: sin schedule → usar amortización & intereses extraídos de la factura
            isLeasing = true;
            const originalFirst = (f.vatBreakdown && f.vatBreakdown[0])
              || { taxableBase: 0, vatRate: 0, vatAmount: 0 };
            const vatRate = parseNumericLike(originalFirst.vatRate);
            const amortBase = parseNumericLike(f.leasingAmortization);
            const interestBase = parseNumericLike(f.leasingInterest);
            // VAT base = amortización + intereses (ambos forman parte de la base imponible)
            const totalVatBase = roundCurrency(amortBase + interestBase);
            const vatOnAmort = roundCurrency((amortBase * vatRate) / 100);
            const vatOnInterest = roundCurrency((totalVatBase * vatRate) / 100 - vatOnAmort);

            // Solo en proveedores con desglose de leasing validado se permite 524.
            // Santander: por contrato; Traton/Scania: por matrícula/contrato del propio proveedor.
            const contractDigits = (f.contractNumber || "").replace(/\D/g, "");
            const last3 =
              supplierAccountCode === "41000727" && contractDigits.length >= 3
                ? contractDigits.slice(-3)
                : "";
            const costRecoveryAccount =
              (last3
                ? leasingData.catalog.find((c) => {
                    if (!c.account.startsWith("524")) return false;
                    const descDigits = (c.description || "").replace(/\D/g, "");
                    return descDigits.includes(last3);
                  })?.account
                : undefined) ||
              leasingData.catalog.find((c) => {
                const desc = normalizeText(c.description || "");
                if (!c.account.startsWith("524")) return false;
                if (!desc) return false;
                const plateNorm = normalizePlateLocal(f.licensePlate);
                const contractNorm = normalizeText(f.contractNumber);
                if (plateNorm && desc.includes(plateNorm)) return true;
                if (contractNorm && desc.includes(contractNorm)) return true;
                return false;
              })?.account || "";
            const supplierDescForInterest =
              leasingData.master.find((m) => m.account_code === supplierAccountCode)?.description;
            const financialChargeAccount =
              pickInterestAccountByDescription(leasingData.catalog, supplierDescForInterest)?.account ||
              leasingData.catalog.find((c) => c.account.startsWith("6623"))?.account || "";

            leasingFields = {
              vatBreakdown: [
                {
                  taxableBase: amortBase,
                  vatRate,
                  vatAmount: vatOnAmort,
                  expenseAccount: costRecoveryAccount,
                },
                {
                  taxableBase: interestBase,
                  vatRate,
                  vatAmount: vatOnInterest,
                  expenseAccount: financialChargeAccount,
                },
              ],
              expenseAccountCode: costRecoveryAccount,
              leasingCostRecovery: undefined,
              leasingFinancialCharge: undefined,
              leasingInsurance: undefined,
              leasingCostRecoveryAccount: undefined,
              leasingFinancialChargeAccount: undefined,
              leasingInsuranceAccount: undefined,
            };
        } else if (leasingData && !f.manualBreakdown && f.leasingAmortization && f.leasingInterest) {
          // Per-supplier leasing default split (no schedule required).
          // E.g. Banco Sabadell: any invoice with "Recuperación de coste" + "Carga financiera"
          // is split into the cost-recovery / financial-charge accounts configured for that NIF.
          const defaultRule = lookupLeasingDefault(leasingData.defaults, f.supplierTaxId);
          if (defaultRule && defaultRule.financial_charge_account) {
            // Resolve cost-recovery 524 account by contract/operation ref digits,
            // falling back to the per-supplier default if no catalog match.
            let costRecoveryAcc = defaultRule.cost_recovery_account || "";
            const contractDigits = String(f.contractNumber || "").replace(/\D/g, "").replace(/^0+/, "");
            if (contractDigits.length >= 4) {
              const found = catalog.find((c) => {
                if (!c.account.startsWith("524")) return false;
                const descDigits = (c.description || "").replace(/\D/g, "");
                if (!descDigits) return false;
                return descDigits.endsWith(contractDigits) || contractDigits.endsWith(descDigits);
              });
              if (found) costRecoveryAcc = found.account;
            }

            if (costRecoveryAcc) {
              isLeasing = true;
              const originalFirst = (f.vatBreakdown && f.vatBreakdown[0])
                || { taxableBase: 0, vatRate: 0, vatAmount: 0 };
              const vatRate = parseNumericLike(originalFirst.vatRate);
              const amortBase = parseNumericLike(f.leasingAmortization);
              const interestBase = parseNumericLike(f.leasingInterest);
              const originalBase = parseNumericLike(originalFirst.taxableBase);
              const sumComponents = amortBase + interestBase;
              // If the invoice base ≈ amort + interest, both bear IVA (typical bank leasing).
              const allTaxable = Math.abs(originalBase - sumComponents) < 0.5 && originalBase > 0;
              const vatOnAmort = roundCurrency((amortBase * vatRate) / 100);
              const vatOnInterest = allTaxable ? roundCurrency((interestBase * vatRate) / 100) : 0;

              leasingFields = {
                vatBreakdown: [
                  {
                    taxableBase: amortBase,
                    vatRate,
                    vatAmount: vatOnAmort,
                    expenseAccount: costRecoveryAcc,
                  },
                  {
                    taxableBase: interestBase,
                    vatRate: allTaxable ? vatRate : 0,
                    vatAmount: vatOnInterest,
                    expenseAccount: defaultRule.financial_charge_account,
                  },
                ],
                expenseAccountCode: costRecoveryAcc,
                leasingCostRecovery: undefined,
                leasingFinancialCharge: undefined,
                leasingInsurance: undefined,
                leasingCostRecoveryAccount: undefined,
                leasingFinancialChargeAccount: undefined,
                leasingInsuranceAccount: undefined,
              };
            }
          }
        }


        const masterAccount = entry?.accountCode?.trim();
        const hasMasterAccount = !!masterAccount;
        const currentExpense = f.expenseAccountCode?.trim() || "";

        // Hierarchy for expense account (non-leasing only)
        let nextExpenseAccount = currentExpense; // (b) preserve manual
        let nextExpenseDescription = f.supplierDescription;

        if (!isLeasing && !currentExpense) {
          // (b.1) Master supplier has explicit contrapartida → apply it directly
          const masterExpense = entry?.expenseAccount?.trim();
          // (c) Plate or contract match in catalog descriptions
          const catalogMatch = findCatalogMatchByPlateOrContract(f.licensePlate, f.contractNumber);
          if (masterExpense) {
            nextExpenseAccount = masterExpense;
            nextExpenseDescription = entry?.description || nextExpenseDescription;
          } else if (catalogMatch) {
            nextExpenseAccount = catalogMatch.account;
            nextExpenseDescription = catalogMatch.description || nextExpenseDescription;
          } else {
            // (c.5) Aggressive auto-assignment from dominant history (≥3 uses, >80% share)
            const dom = findDominantHistoryMatch(f.supplierTaxId);
            if (dom) {
              nextExpenseAccount = dom.account;
              nextExpenseDescription = dom.description || nextExpenseDescription;
            } else if (catalog.length > 0 && f.file && !f.aiSuggestionAttempted) {
              // (d) AI + (e) history fallback handled async below.
              // Solo una vez por documento: si la IA ya la analizó y no hubo cuenta,
              // no se vuelve a consultar en cada refresco de la sesión.
              needsSuggestion.push({ id: f.id, file: f.file });
            }
          }
        }

        // Si NO es leasing y tenemos una cuenta de gasto, propagar a TODAS las líneas de IVA
        // (el usuario revisará y ajustará manualmente si alguna línea necesita cuenta distinta).
        let propagatedBreakdown: ProcessedFile["vatBreakdown"] | undefined;
        if (!isLeasing && nextExpenseAccount && f.vatBreakdown && f.vatBreakdown.length > 0) {
          propagatedBreakdown = f.vatBreakdown.map((line) => ({
            ...line,
            expenseAccount: (line.expenseAccount && line.expenseAccount.trim()) ? line.expenseAccount : nextExpenseAccount,
          }));
        }

        return {
          ...f,
          supplierAccountCode: hasMasterAccount ? masterAccount! : f.supplierAccountCode,
          expenseAccountCode: isLeasing ? (leasingFields.expenseAccountCode as string) : nextExpenseAccount,
          supplierDescription: hasMasterAccount ? (entry?.description ?? f.supplierDescription) : nextExpenseDescription,
          operationType: applyReceivedTaxDefaults(f, entry?.operationType).operationType,
          retentionType: entry?.retentionType ?? f.retentionType ?? "none",
          postalCode: (f.postalCode && f.postalCode.trim()) ? f.postalCode : (entry?.postalCode || f.postalCode),
          ...(propagatedBreakdown ? { vatBreakdown: propagatedBreakdown } : {}),
          ...leasingFields,
        };
      }),
    );

    if (needsSuggestion.length === 0) return;

    // Async AI suggestions (max 3 concurrent), with history fallback
    const catalogPayload = catalog
      .filter((c) => !c.account.trim().startsWith("524"))
      .map((c) => ({ account: c.account, description: c.description }));
    const runSuggestion = async ({ id, file }: { id: string; file: File }) => {
      let aiAccount = "";
      try {
        let payload: any = { catalog: catalogPayload, companyId: appliedCompanyRef.current?.id ?? null };
        if (isImageFile(file)) {
          const images = await fileToBase64Images(file);
          payload.images = images;
        } else {
          const { images, text } = await extractFromPdf(file);
          payload.images = images;
          payload.text = text;
        }
        const current = processedRef.current.find((p) => p.id === id);
        if (current) {
          payload.supplierName = current.supplier;
          payload.licensePlate = current.licensePlate;
          payload.contractNumber = current.contractNumber;
        }
        const { data, error } = await supabase.functions.invoke("suggest-expense-account", { body: payload });
        if (!error && data?.account && catalogAccountSet.has(String(data.account).trim())) {
          aiAccount = String(data.account).trim();
        }
      } catch (e) {
        console.error("suggest-expense-account failed", e);
      }

      let chosen = aiAccount;
      let chosenDesc: string | undefined;
      if (!chosen) {
        const current = processedRef.current.find((p) => p.id === id);
        const hist = findHistoryMatch(current?.supplierTaxId);
        if (hist) {
          chosen = hist.account;
          chosenDesc = hist.description;
        }
      }
      if (!chosen) {
        // Consultada sin resultado: marcar igualmente para no repetir la llamada.
        setProcessed((prev) => prev.map((p) => (p.id === id ? { ...p, aiSuggestionAttempted: true } : p)));
        return;
      }

      setProcessed((prev) =>
        prev.map((p) => {
          if (p.id !== id) return p;
          if (p.expenseAccountCode?.trim()) return { ...p, aiSuggestionAttempted: true };
          // Propagar la cuenta sugerida también a TODAS las líneas de IVA vacías.
          const breakdown = (p.vatBreakdown && p.vatBreakdown.length > 0)
            ? p.vatBreakdown.map((line) => ({
                ...line,
                expenseAccount: (line.expenseAccount && line.expenseAccount.trim()) ? line.expenseAccount : chosen,
              }))
            : p.vatBreakdown;
          return {
            ...p,
            aiSuggestionAttempted: true,
            expenseAccountCode: chosen,
            supplierDescription: p.supplierDescription || chosenDesc || "",
            ...(breakdown ? { vatBreakdown: breakdown } : {}),
          };
        }),
      );
    };

    const concurrency = 3;
    for (let i = 0; i < needsSuggestion.length; i += concurrency) {
      await Promise.all(needsSuggestion.slice(i, i + concurrency).map(runSuggestion));
    }
  }, []);

  const handleUpdateExpenseAccount = useCallback((fileId: string, value: string, vatLineIndex?: number) => {
    setProcessed((prev) =>
      prev.map((f) => {
        if (f.id !== fileId) return f;
        // Per-VAT-line update: only mutate that line's expenseAccount
        if (typeof vatLineIndex === "number" && Number.isInteger(vatLineIndex) && vatLineIndex >= 0) {
          const breakdown = (f.vatBreakdown || []).map((line, idx) =>
            idx === vatLineIndex ? { ...line, expenseAccount: value } : line,
          );
          // Mirror line 0 onto file-level field for backward compat (Excel, history, single-line cases)
          const next = { ...f, vatBreakdown: breakdown, manualBreakdown: true };
          if (vatLineIndex === 0) next.expenseAccountCode = value;
          return next;
        }
        return { ...f, expenseAccountCode: value };
      }),
    );
  }, []);

  const handleUpdateOperationType = useCallback(
    (fileId: string, operationType: "interior" | "aib" | "ais" | "isp" | "importacion") => {
      setProcessed((prev) =>
        prev.map((f) => (f.id === fileId ? setReceivedOperationType(f, operationType) : f)),
      );
    },
    [],
  );

  const handleUpdateRetentionType = useCallback(
    (
      fileId: string,
      retentionType: "none" | "mod111" | "mod115" | "mod193",
    ) => {
      setProcessed((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, retentionType } : f)),
      );
    },
    [],
  );

  const handleUpdateVatDeductibility = useCallback(
    (fileId: string, vatDeductibility: "full" | "none" | "half") => {
      setProcessed((prev) =>
        prev.map((f) => (f.id === fileId ? setReceivedVatDeductibility(f, vatDeductibility) : f)),
      );
    },
    [],
  );



  /** Marca/desmarca abono: invierte el signo de bases, cuotas, IRPF y total. */
  const handleToggleCreditNote = useCallback((fileId: string, value: boolean) => {
    setProcessed((prev) =>
      prev.map((f) => {
        if (f.id !== fileId || !!f.isCreditNote === value) return f;
        const flip = (n?: number) =>
          typeof n === "number" && Number.isFinite(n) ? Math.round(-n * 100) / 100 : n;
        return {
          ...f,
          isCreditNote: value,
          vatBreakdown: (f.vatBreakdown ?? []).map((l) => ({
            ...l,
            taxableBase: flip(l.taxableBase) as number,
            vatAmount: flip(l.vatAmount) as number,
          })),
          totalAmount: flip(f.totalAmount),
          irpfAmount: flip(f.irpfAmount),
        };
      }),
    );
  }, []);

  const handleUpdateSupplierAccount = useCallback((fileId: string, value: string) => {
    setProcessed((prev) => prev.map((f) => (f.id === fileId ? { ...f, supplierAccountCode: value } : f)));
  }, []);

  const handleQuickSupplierCreated = useCallback((fileId: string, created: QuickSupplierCreated) => {
    supplierMasterRef.current = new Map(supplierMasterRef.current || new Map<string, SupplierMasterEntry>());
    supplierMasterRef.current.set(created.taxId, created.entry);
    const normalizeTaxId = (value?: string) => (value || "").replace(/[\s.-]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");
    const targetTaxId = normalizeTaxId(created.taxId);
    const masterName = (created.entry.description || "").trim();

    setProcessed((prev) => {
      const affectedIds = new Set<string>([fileId]);
      prev.forEach((f) => {
        if (normalizeTaxId(f.supplierTaxId) === targetTaxId) affectedIds.add(f.id);
      });

      const updated = prev.map((f) => {
        if (!affectedIds.has(f.id)) return f;
        const withCore = updateProcessedCoreFields(f, {
          supplier: masterName || f.supplier,
          invoiceNumber: f.invoiceNumber,
        });
        const breakdown = (withCore.vatBreakdown || []).map((line, idx) => {
          if (idx !== 0) return line;
          return created.expenseAccount ? { ...line, expenseAccount: created.expenseAccount } : line;
        });
        return {
          ...withCore,
          supplierTaxId: created.taxId,
          supplierAccountCode: created.accountCode,
          expenseAccountCode: created.expenseAccount || withCore.expenseAccountCode,
          supplierDescription: f.id === fileId && created.description ? created.description : withCore.supplierDescription,
          operationType: applyReceivedTaxDefaults(withCore, created.entry.operationType).operationType,
          retentionType: created.entry.retentionType ?? withCore.retentionType ?? "none",
          postalCode: created.postalCode || f.postalCode,
          ...(breakdown.length > 0 ? { vatBreakdown: breakdown } : {}),
          receiverConfusion: false,
        };
      });
      return recheckAllDuplicates(recalculateNames(updated, prefixRef.current, startNumberRef.current));
    });

    if (appliedCompany) {
      window.setTimeout(() => { void handleApplyMaster(appliedCompany); }, 0);
    }
  }, [appliedCompany, handleApplyMaster, recalculateNames, recheckAllDuplicates, updateProcessedCoreFields]);

  // Modifica el asiento de pago de una factura (generar sí/no, cuenta de
  // contrapartida, tipo). Si se desactiva, se limpian cuenta y tipo.
  const handleUpdatePaymentEntry = useCallback(
    (
      fileId: string,
      fields: { generatePaymentEntry?: boolean; paymentEntryAccount?: string; paymentEntryKind?: "cash" | "card" },
    ) => {
      setProcessed((prev) => {
        const updated = prev.map((f) => {
          if (f.id !== fileId) return f;
          const next = { ...f };
          if (fields.generatePaymentEntry !== undefined) {
            next.generatePaymentEntry = fields.generatePaymentEntry;
            if (!fields.generatePaymentEntry) {
              next.paymentEntryAccount = "";
              next.paymentEntryKind = undefined;
              // Al desmarcar la casilla, eliminamos también la "Forma de pago"
              // para que el sufijo _CAJA / _TJxxxx desaparezca del nombre del archivo.
              return updateProcessedCoreFields(next, { paymentMethod: "" });
            }
          }
          if (fields.paymentEntryAccount !== undefined) {
            next.paymentEntryAccount = fields.paymentEntryAccount;
          }
          if (fields.paymentEntryKind !== undefined) {
            next.paymentEntryKind = fields.paymentEntryKind;
          }
          // Sincroniza la "Forma de pago" para que aparezca en el Excel del Libro de IVA
          // cuando el usuario selecciona caja/tarjeta manualmente desde el split view.
          // Solo se rellena/sobreescribe si el usuario eligió una cuenta de pago concreta.
          if (next.generatePaymentEntry && next.paymentEntryAccount) {
            const pa = paymentAccountsRef.current;
            let derived = "";
            if (next.paymentEntryKind === "cash") {
              derived = "CAJA";
            } else if (next.paymentEntryKind === "card") {
              const card = pa?.cardAccounts?.find((c) => c.account === next.paymentEntryAccount);
              const source = card?.label || card?.account || next.paymentEntryAccount;
              const digits = (source.match(/\d{4}/g) || []).pop();
              derived = digits ? `TJ${digits}` : (card?.label || "TARJETA").toUpperCase();
            }
            if (derived) {
              return updateProcessedCoreFields(next, { paymentMethod: derived });
            }
          }
          return next;
        });
        return recalculateNames(updated, prefixRef.current, startNumberRef.current);
      });
    },
    [recalculateNames, updateProcessedCoreFields],
  );

  // Pure utility functions moved outside to keep useCallback references stable — see module-level definitions above

  const logNumericUpdateFailure = (
    reason: "file_not_found" | "invalid_vat_line_index",
    context: {
      fileId: string;
      field: "taxableBase" | "vatRate" | "vatAmount" | "irpfRate" | "irpfAmount" | "totalAmount";
      value: number | string;
      vatLineIndex?: number;
      availableVatLines?: number;
    },
  ) => {
    console.warn("[IVA update skipped]", {
      reason,
      ...context,
    });
  };

  const recalcConsistency = useCallback((file: ProcessedFile): ProcessedFile => {
    const bd = cloneVatBreakdown(file.vatBreakdown);

    const sumBase = bd.reduce((sum, line) => sum + line.taxableBase, 0);
    const sumVat = bd.reduce((sum, line) => sum + line.vatAmount, 0);
    const irpf = file.irpfAmount === undefined ? undefined : toSafeNumber(file.irpfAmount);
    const total = file.totalAmount === undefined ? undefined : toSafeNumber(file.totalAmount);
    const irpfRate = file.irpfRate === undefined ? undefined : toSafeNumber(file.irpfRate);
    const calc = roundCurrency(sumBase + sumVat - (irpf ?? 0));
    const inconsistent = (total ?? 0) > 0 && Math.abs((total ?? 0) - calc) > 0.05;

    // El aviso de abono depende del signo de los importes: si el usuario los
    // pone en positivo se quita solo; si los pone en negativo vuelve a salir.
    const hasNegativeAmounts =
      sumBase < 0 || sumVat < 0 || (total ?? 0) < 0 || (irpf ?? 0) < 0;

    return {
      ...file,
      vatBreakdown: bd,
      irpfRate,
      irpfAmount: irpf,
      totalAmount: total,
      isCreditNote: hasNegativeAmounts,
      creditNoteReviewed:
        file.creditNoteReviewed || (file.isCreditNote !== undefined && !!file.isCreditNote !== hasNegativeAmounts),
      totalInconsistency: inconsistent,
      calculatedTotal: calc,
    };
  }, []);

  const handleUpdateNumericField = useCallback(
    (
      fileId: string,
      field: "taxableBase" | "vatRate" | "vatAmount" | "irpfRate" | "irpfAmount" | "totalAmount",
      value: number | string,
      vatLineIndex?: number,
    ) => {
      const numericValue = Number(value);
      const safeValue = Number.isNaN(numericValue) ? 0 : numericValue;

      setProcessed((prev) => {
        let matchedFile = false;
        let updatedFile = false;
        let failureContext:
          | {
              reason: "invalid_vat_line_index";
              availableVatLines: number;
            }
          | undefined;

        const next = prev.map((f) => {
          if (f.id !== fileId) return f;

          matchedFile = true;

          const nextFile = cloneProcessedFileForNumericUpdate(f);
          const breakdown = nextFile.vatBreakdown ?? cloneVatBreakdown(f.vatBreakdown);

          const syncDerivedTotals = (forceSyncTotal = false, opts?: { skipIrpfRecalc?: boolean }) => {
            const taxableBaseSum = breakdown.reduce((sum, line) => sum + line.taxableBase, 0);
            const vatSum = breakdown.reduce((sum, line) => sum + line.vatAmount, 0);

            if (!opts?.skipIrpfRecalc && nextFile.irpfRate !== undefined) {
              nextFile.irpfRate = toSafeNumber(nextFile.irpfRate);
              // IRPF sólo sobre bases sujetas a IVA (excluye suplidos / exentos con % IVA = 0).
              const irpfBaseSum = computeIrpfTaxableBase(breakdown, taxableBaseSum);
              nextFile.irpfAmount = roundCurrency((irpfBaseSum * nextFile.irpfRate) / 100);
            }

            const calculatedTotal = roundCurrency(taxableBaseSum + vatSum - (nextFile.irpfAmount ?? 0));

            // El TOTAL de la factura es el impreso en el documento: editar bases, tipos o
            // retención NO lo sobreescribe. Sólo se rellena si aún no hay total detectado.
            if (forceSyncTotal && !(nextFile.totalAmount ?? 0)) {
              nextFile.totalAmount = calculatedTotal;
            }

            nextFile.calculatedTotal = calculatedTotal;
            nextFile.totalInconsistency =
              (nextFile.totalAmount ?? 0) > 0 && Math.abs((nextFile.totalAmount ?? 0) - calculatedTotal) > 0.05;
          };

          if (["taxableBase", "vatRate", "vatAmount"].includes(field) && vatLineIndex !== undefined) {
            if (!Number.isInteger(vatLineIndex) || vatLineIndex < 0) {
              failureContext = {
                reason: "invalid_vat_line_index",
                availableVatLines: breakdown.length,
              };
              return f;
            }

            while (breakdown.length <= vatLineIndex) {
              breakdown.push({ taxableBase: 0, vatRate: 0, vatAmount: 0 });
            }

            const line = breakdown[vatLineIndex];

            if (field === "vatRate") {
              const rate = normalizeVatRate(safeValue);
              line.vatRate = rate;
              line.vatAmount = roundCurrency((line.taxableBase * rate) / 100);
            } else if (field === "taxableBase") {
              line.taxableBase = safeValue;
              line.vatAmount = roundCurrency((safeValue * line.vatRate) / 100);
            } else {
              line.vatAmount = safeValue;
            }

            nextFile.vatBreakdown = breakdown;
            nextFile.manualBreakdown = true;
            syncDerivedTotals(true);
            updatedFile = true;
            return recalcConsistency(nextFile);
          }

          if (["taxableBase", "vatRate", "vatAmount"].includes(field)) {
            failureContext = {
              reason: "invalid_vat_line_index",
              availableVatLines: breakdown.length,
            };
            return f;
          }

          if (field === "irpfRate") {
            nextFile.irpfRate = safeValue;
            syncDerivedTotals(true);
            updatedFile = true;
            return recalcConsistency(nextFile);
          }

          if (field === "irpfAmount") {
            nextFile.irpfAmount = safeValue;
            // Edición manual de la cuota IRPF (típico en notaría con suplidos que no computan
            // ni para IVA ni para retención): NO recalcular desde base × %.
            syncDerivedTotals(true, { skipIrpfRecalc: true });
            updatedFile = true;
            return recalcConsistency(nextFile);
          }

          if (field === "totalAmount") {
            nextFile.totalAmount = safeValue;
            updatedFile = true;
            return recalcConsistency(nextFile);
          }

          updatedFile = true;
          return recalcConsistency({ ...nextFile, [field]: safeValue });
        });

        if (!matchedFile) {
          logNumericUpdateFailure("file_not_found", { fileId, field, value, vatLineIndex });
          return prev;
        }

        if (!updatedFile && failureContext) {
          logNumericUpdateFailure(failureContext.reason, {
            fileId,
            field,
            value,
            vatLineIndex,
            availableVatLines: failureContext.availableVatLines,
          });
          return prev;
        }

        return next;
      });
    },
    [recalcConsistency],
  );

  const handleUpdateDescription = useCallback((fileId: string, value: string) => {
    setProcessed((prev) => prev.map((f) => (f.id === fileId ? { ...f, supplierDescription: value } : f)));
  }, []);

  const handleUpdateNotes = useCallback((fileId: string, value: string) => {
    setProcessed((prev) => prev.map((f) => (f.id === fileId ? { ...f, notes: value } : f)));
  }, []);

  const handleUpdateAttachments = useCallback((fileId: string, files: File[]) => {
    setProcessed((prev) => prev.map((f) => (f.id === fileId ? { ...f, attachments: files } : f)));
  }, []);

  const handleAddVatLine = useCallback((fileId: string) => {
    setProcessed((prev) =>
      prev.map((f) => {
        if (f.id !== fileId) return f;
        // Al pasar de 1 → N líneas, espejear la cuenta de contrapartida a nivel factura
        // en la línea 0 si esa línea aún no tiene `expenseAccount` propia. Así el usuario
        // no pierde la contrapartida cuando crea una segunda línea de IVA.
        const existing = (f.vatBreakdown && f.vatBreakdown.length > 0)
          ? f.vatBreakdown
          : [{ taxableBase: 0, vatRate: 0, vatAmount: 0 }];
        const fileLevel = (f.expenseAccountCode || "").trim();
        const mirrored = existing.map((line, i) => {
          const lineAcc = (line.expenseAccount || "").trim();
          if (i === 0 && !lineAcc && fileLevel) return { ...line, expenseAccount: fileLevel };
          return { ...line };
        });
        const breakdown = [...mirrored, { taxableBase: 0, vatRate: 21, vatAmount: 0 }];
        return recalcConsistency({ ...f, vatBreakdown: breakdown, manualBreakdown: true });
      }),
    );
  }, [recalcConsistency]);

  const handleRemoveVatLine = useCallback((fileId: string, vatLineIndex: number) => {
    setProcessed((prev) =>
      prev.map((f) => {
        if (f.id !== fileId) return f;
        const breakdown = [...(f.vatBreakdown || [])];
        if (breakdown.length <= 1) return f;
        breakdown.splice(vatLineIndex, 1);
        return recalcConsistency({ ...f, vatBreakdown: breakdown, manualBreakdown: true });
      }),
    );
  }, [recalcConsistency]);

  const handleUpdateVatBookField = useCallback(
    (
      fileId: string,
      field: "date" | "supplier" | "supplierTaxId" | "invoiceNumber" | "paymentMethod",
      value: string,
    ) => {
      setProcessed((prev) => {
        const updated = prev.map((f) => {
          if (f.id !== fileId) return f;
          if (field === "date" || field === "supplier" || field === "invoiceNumber" || field === "paymentMethod") {
            return updateProcessedCoreFields(f, { [field]: value } as Partial<
              Pick<ProcessedFile, "date" | "supplier" | "invoiceNumber" | "paymentMethod">
            >);
          }
          return { ...f, supplierTaxId: value };
        });
        const renamed = recalculateNames(updated, prefixRef.current, startNumberRef.current);
        // Re-check duplicates when identity fields change
        if (field === "supplierTaxId" || field === "invoiceNumber") {
          return recheckAllDuplicates(renamed);
        }
        return renamed;
      });
    },
    [recalculateNames, updateProcessedCoreFields, recheckAllDuplicates],
  );


  const startEditingFailed = (id: string) => {
    setFailedFiles((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, editing: true, editDate: "", editSupplier: "", editInvoiceNumber: "" } : f,
      ),
    );
  };

  const cancelEditingFailed = (id: string) => {
    setFailedFiles((prev) => prev.map((f) => (f.id === id ? { ...f, editing: false } : f)));
  };

  const updateFailedField = (id: string, field: string, value: string) => {
    const isNumericFailedField = [
      "editTaxableBase",
      "editVatRate",
      "editVatAmount",
      "editIrpfRate",
      "editIrpfAmount",
      "editTotalAmount",
    ].includes(field);

    const nextValue =
      isNumericFailedField && /[^\d,\.\-+\s]/.test(value)
        ? String(parseNumericLike(value))
        : value;

    setFailedFiles((prev) => prev.map((f) => (f.id === id ? { ...f, [field]: nextValue } : f)));
  };

  const [ticketMode, setTicketMode] = useState(false);
  const ticketModeRef = useRef(false);
  useEffect(() => { ticketModeRef.current = ticketMode; }, [ticketMode]);

  const validateFailed = (
    ff: FailedFile,
    vatLinesDraft?: { taxableBase: string; vatRate: string; vatAmount: string; expenseAccount?: string }[],
  ) => {
    const date = ff.editDate || "";
    const supplier = ff.editSupplier || "";
    const invoiceNumber = normalizeInvoiceNumber(ff.editInvoiceNumber || "");
    if (!date || !supplier || !invoiceNumber) return;

    const formattedDate = date.replace(/-/g, "");
    const paymentMethod = ff.editPaymentMethod || detectPaymentMethod(ff.file.name);
    const ext = getFileExtension(ff.file.name);
    const paymentSuffix = paymentMethod ? `_${paymentMethod}` : "";
    const baseName = buildBaseName(formattedDate, supplier, invoiceNumber, paymentSuffix, ext);

    // Construye las líneas de IVA: si vienen del split view (multi-línea) las
    // usa; si no, recae en los campos legacy (single line del formulario inline).
    const sourceLines =
      vatLinesDraft && vatLinesDraft.length > 0
        ? vatLinesDraft
        : [{
            taxableBase: ff.editTaxableBase || "0",
            vatRate: ff.editVatRate || "0",
            vatAmount: ff.editVatAmount || "0",
            expenseAccount: undefined as string | undefined,
          }];

    const vatBreakdown = sourceLines
      .map((ln) => ({
        taxableBase: parseNumericLike(ln.taxableBase),
        vatRate: parseNumericLike(ln.vatRate),
        vatAmount: parseNumericLike(ln.vatAmount),
        expenseAccount: ln.expenseAccount?.trim() || undefined,
      }))
      .filter((ln) => ln.taxableBase || ln.vatRate || ln.vatAmount || ln.expenseAccount);

    const irpfRate = parseNumericLike(ff.editIrpfRate || "0");
    const irpfAmount = parseNumericLike(ff.editIrpfAmount || "0");
    const totalAmount = parseNumericLike(ff.editTotalAmount || "0");

    const entry: ProcessedFile = {
      id: ff.id,
      originalName: ff.file.name,
      baseName,
      newName: "",
      date,
      supplier: supplier.trim(),
      invoiceNumber: invoiceNumber.trim(),
      paymentMethod,
      supplierTaxId: ff.editSupplierTaxId || "",
      supplierAccountCode: ff.editSupplierAccount?.trim() || undefined,
      supplierDescription: ff.editDescription?.trim() || undefined,

      vatBreakdown,
      totalAmount,
      irpfRate,
      irpfAmount,
      file: ff.file,
      manualBreakdown: true,
    };


    entry.isDuplicate = checkDuplicate(
      entry.supplierTaxId || "",
      entry.invoiceNumber,
      ff.file.name,
      entry.supplier,
      currentUserId,
    );

    setFailedFiles((prev) => prev.filter((f) => f.id !== ff.id));
    setProcessed((prev) => {
      const next = recheckAllDuplicates([...prev, entry]);
      return recalculateNames(next, prefixRef.current, startNumberRef.current);
    });
  };

  /**
   * Promociona un archivo fallido a la lista de "procesados" con campos vacíos,
   * de modo que el usuario pueda cumplimentarlo con la misma UI (ProcessedFileSplitView)
   * que un documento reconocido correctamente. Se abre inmediatamente el editor.
   */
  // Guardamos el FailedFile original al promover, para poder revertir si el usuario cierra sin guardar.
  const pendingFailedBackupRef = useRef<Map<string, FailedFile>>(new Map());

  const promoteFailedToProcessed = (ff: FailedFile) => {
    const ext = getFileExtension(ff.file.name);
    const entry: ProcessedFile = {
      id: ff.id,
      originalName: ff.file.name,
      baseName: `SIN_DATOS.${ext}`,
      newName: "",
      date: ff.editDate || "",
      supplier: (ff.editSupplier || "").trim(),
      invoiceNumber: normalizeInvoiceNumber(ff.editInvoiceNumber || ""),
      supplierTaxId: ff.editSupplierTaxId || "",
      paymentMethod: ff.editPaymentMethod || detectPaymentMethod(ff.file.name),
      supplierAccountCode: ff.editSupplierAccount?.trim() || undefined,
      supplierDescription: ff.editDescription?.trim() || undefined,
      vatBreakdown: [{
        taxableBase: parseNumericLike(ff.editTaxableBase || "0"),
        vatRate: parseNumericLike(ff.editVatRate || "21"),
        vatAmount: parseNumericLike(ff.editVatAmount || "0"),
      }],
      totalAmount: parseNumericLike(ff.editTotalAmount || "0"),
      irpfRate: parseNumericLike(ff.editIrpfRate || "0"),
      irpfAmount: parseNumericLike(ff.editIrpfAmount || "0"),
      file: ff.file,
      manualBreakdown: true,
      pendingValidation: true,
    };

    pendingFailedBackupRef.current.set(ff.id, ff);
    setFailedFiles((prev) => prev.filter((f) => f.id !== ff.id));
    setProcessed((prev) => {
      const next = recheckAllDuplicates([...prev, entry]);
      return recalculateNames(next, prefixRef.current, startNumberRef.current);
    });
    setOpenInvoiceId(entry.id);
  };

  /** Cuando se cierra el split view sin haber pulsado "Guardar", revertimos el documento a "No procesados". */
  const handleSplitViewClose = () => {
    const closingId = openInvoiceId;
    setOpenInvoiceId(null);
    if (!closingId) return;
    // Usamos setProcessed funcional para leer el estado más reciente (incluyendo el
    // setProcessed que handleUpdateFile acaba de encolar al pulsar "Guardar"). De lo
    // contrario processedRef.current podría estar desactualizado y revertiríamos
    // una factura que el usuario acaba de validar.
    setProcessed((prev) => {
      const pf = prev.find((f) => f.id === closingId);
      if (!pf || !pf.pendingValidation) return prev;
      const backup = pendingFailedBackupRef.current.get(closingId);
      pendingFailedBackupRef.current.delete(closingId);
      if (backup) {
        setFailedFiles((fPrev) => [...fPrev, backup]);
      }
      const next = prev.filter((f) => f.id !== closingId);
      return recalculateNames(next, prefixRef.current, startNumberRef.current);
    });
  };

  const sanitize = (s: string) =>
    s
      .trim()
      .replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ _-]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();

  const normalizeLicensePlate = (value?: string): string | undefined => {
    if (!value) return undefined;

    const compact = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!compact) return undefined;

    if (/^R\d{4}[A-Z]{3}$/.test(compact)) {
      return `R-${compact.slice(1, 5)}-${compact.slice(5)}`;
    }

    if (/^[A-Z]\d{4}[A-Z]{2}$/.test(compact)) {
      return `${compact[0]}-${compact.slice(1, 5)}-${compact.slice(5)}`;
    }

    if (/^\d{4}[A-Z]{3}$/.test(compact)) {
      return compact;
    }

    if (/^\d{4}[A-Z]{2}$/.test(compact)) {
      return compact;
    }

    return compact.length >= 5 ? compact : undefined;
  };

  const extractLicensePlateFallback = (value?: string): string | undefined => {
    if (!value) return undefined;

    const patterns = [
      /\((R[-\s]?\d{4}[-\s]?[A-Z]{3})\)/i,
      /\((\d{4}[-\s]?[A-Z]{3})\)/i,
      /\(([A-Z][-\s]?\d{4}[-\s]?[A-Z]{2})\)/i,
      /\b(R[-\s]?\d{4}[-\s]?[A-Z]{3})\b/i,
      /\b(\d{4}[-\s]?[A-Z]{3})\b/i,
      /\b([A-Z][-\s]?\d{4}[-\s]?[A-Z]{2})\b/i,
    ];

    for (const pattern of patterns) {
      const match = value.match(pattern);
      if (match?.[1]) {
        return normalizeLicensePlate(match[1]);
      }
    }

    return undefined;
  };


  const sanitizeProcessedCollection = (files: ProcessedFile[]) => {
    let changed = files.some((file) => !file);

    const next = files.filter(Boolean).map((file) => {
      const nextBreakdown = (Array.isArray(file.vatBreakdown) ? file.vatBreakdown : []).map((line) => ({
        taxableBase: parseNumericLike(line?.taxableBase),
        vatRate: normalizeVatRate(line?.vatRate),
        vatAmount: parseNumericLike(line?.vatAmount),
        expenseAccount: typeof line?.expenseAccount === "string" ? line.expenseAccount : undefined,
      }));
      const nextTotalAmount = file.totalAmount === undefined ? undefined : parseNumericLike(file.totalAmount);
      const nextIrpfRate = file.irpfRate === undefined ? undefined : parseNumericLike(file.irpfRate);
      const nextIrpfAmount = file.irpfAmount === undefined ? undefined : parseNumericLike(file.irpfAmount);
      const nextCalculatedTotal = file.calculatedTotal === undefined ? undefined : parseNumericLike(file.calculatedTotal);
      const nextDate = toSafeText(file.date);
      const nextSupplier = toSafeText(file.supplier);
      const nextInvoiceNumber = normalizeInvoiceNumber(file.invoiceNumber);
      const nextOriginalName = toSafeText(file.originalName) || "factura.pdf";
      const nextBaseName = toSafeText(file.baseName);
      const nextNewName = toSafeText(file.newName);

      const hasBreakdownChanges = nextBreakdown.some((line, index) => {
        const current = file.vatBreakdown?.[index];
        return (
          current?.taxableBase !== line.taxableBase ||
          current?.vatRate !== line.vatRate ||
          current?.vatAmount !== line.vatAmount ||
          current?.expenseAccount !== line.expenseAccount
        );
      });

      if (
        hasBreakdownChanges ||
        file.totalAmount !== nextTotalAmount ||
        file.irpfRate !== nextIrpfRate ||
        file.irpfAmount !== nextIrpfAmount ||
        file.calculatedTotal !== nextCalculatedTotal ||
        file.date !== nextDate ||
        file.supplier !== nextSupplier ||
        file.invoiceNumber !== nextInvoiceNumber ||
        file.originalName !== nextOriginalName ||
        file.baseName !== nextBaseName ||
        file.newName !== nextNewName
      ) {
        changed = true;
        return {
          ...file,
          originalName: nextOriginalName,
          baseName: nextBaseName,
          newName: nextNewName,
          date: nextDate,
          supplier: nextSupplier,
          invoiceNumber: nextInvoiceNumber,
          vatBreakdown: nextBreakdown,
          totalAmount: nextTotalAmount,
          irpfRate: nextIrpfRate,
          irpfAmount: nextIrpfAmount,
          calculatedTotal: nextCalculatedTotal,
        };
      }

      return file;
    });

    return changed ? next : files;
  };

  const sanitizeFailedCollection = (files: FailedFile[]) => {
    let changed = false;
    const numericFields = [
      "editTaxableBase",
      "editVatRate",
      "editVatAmount",
      "editIrpfRate",
      "editIrpfAmount",
      "editTotalAmount",
    ] as const;

    const next = files.map((file) => {
      let updatedFile = file;

      for (const field of numericFields) {
        const rawValue = file[field];
        if (typeof rawValue === "string" && /[^\d,\.\-+\s]/.test(rawValue)) {
          const cleanedValue = String(parseNumericLike(rawValue));
          updatedFile = updatedFile === file ? { ...file } : updatedFile;
          updatedFile[field] = cleanedValue;
          changed = true;
        }
      }

      return updatedFile;
    });

    return changed ? next : files;
  };

  const detectPaymentMethod = (fileName: string): string | undefined => {
    // Quitar la extensión para no contaminar el matching.
    const nameNoExt = fileName.replace(/\.[^.]+$/, "");
    // Solo aceptamos la palabra clave como token completo, delimitado por
    // separadores típicos del nombre (_ - espacio . inicio/fin). Evita que
    // dígitos/underscores adyacentes (p.ej. "178954_EFECTIVO") se incluyan.
    // Palabras clave de forma de pago. Las que pueden ir seguidas de un
    // número (p.ej. "TJ6029", "TARJETA1234", "CTJ12") se admiten con dígitos
    // adyacentes opcionales. El resto (CAJA, EFECTIVO, CONTADO) deben ir
    // como token aislado para no contaminar con dígitos del número de factura.
    const KEYWORDS_WITH_DIGITS = "(?:tarjeta|ctj|tj)\\d*";
    const KEYWORDS_STANDALONE = "(?:caja|efectivo|contado)";
    const re = new RegExp(
      `(?:^|[_\\-\\s.])(${KEYWORDS_WITH_DIGITS}|${KEYWORDS_STANDALONE})(?=[_\\-\\s.]|$)`,
      "i",
    );
    const match = nameNoExt.match(re);
    const method = match ? match[1].toUpperCase() : undefined;
    // Si el nombre original contiene "PAGADA", se conserva como sufijo adicional.
    const hasPagada = /(?:^|[_\-\s.])pagada(?=[_\-\s.]|$)/i.test(nameNoExt);
    if (hasPagada) {
      return method ? `${method}_PAGADA` : "PAGADA";
    }
    return method;
  };

  const MAX_BASENAME_LEN = 120;

  const buildBaseName = (
    formattedDate: string,
    supplier: string,
    invoiceNumber: string,
    paymentSuffix: string,
    ext: string,
    licensePlate?: string,
  ): string => {
    const sanSupplier = sanitize(supplier);
    const sanInvoice = sanitize(invoiceNumber).toUpperCase();
    const normalizedPlate = normalizeLicensePlate(licensePlate);
    const platePart = normalizedPlate ? `_${sanitize(normalizedPlate)}` : "";
    let candidate = `${formattedDate}_${sanSupplier}_${sanInvoice}${platePart}${paymentSuffix}.${ext}`;
    if (candidate.length > MAX_BASENAME_LEN) {
      // First try: trim invoice number
      const fixedLen = `${formattedDate}_${sanSupplier}_${platePart}${paymentSuffix}.${ext}`.length + 1;
      const available = MAX_BASENAME_LEN - fixedLen;
      const trimmed = available > 0 ? sanInvoice.slice(-available) : sanInvoice.slice(-3);
      candidate = `${formattedDate}_${sanSupplier}_${trimmed}${platePart}${paymentSuffix}.${ext}`;
    }
    if (candidate.length > MAX_BASENAME_LEN) {
      // Second try: drop invoice number entirely
      candidate = `${formattedDate}_${sanSupplier}${platePart}${paymentSuffix}.${ext}`;
    }
    if (candidate.length > MAX_BASENAME_LEN) {
      // Third try: also trim supplier name
      const fixedLen = `${formattedDate}_${platePart}${paymentSuffix}.${ext}`.length + 1;
      const available = MAX_BASENAME_LEN - fixedLen;
      const trimmedSupplier = available > 0 ? sanSupplier.slice(0, available) : sanSupplier.slice(0, 20);
      candidate = `${formattedDate}_${trimmedSupplier}${platePart}${paymentSuffix}.${ext}`;
    }
    return candidate;
  };

  const isImageFile = (file: File): boolean => {
    return file.type === "image/png" || file.type === "image/jpeg";
  };

  const hasSupportedExtension = (fileName: string): boolean => {
    const name = fileName.toLowerCase();
    return name.endsWith(".pdf") || name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg");
  };

  const isSupportedInvoiceFile = async (file: File): Promise<boolean> => {
    const mime = (file.type || "").toLowerCase();

    if (
      mime === "application/pdf" ||
      mime === "image/png" ||
      mime === "image/jpeg" ||
      hasSupportedExtension(file.name)
    ) {
      return true;
    }

    // Fallback for published environments/browsers where MIME and extension may be missing
    try {
      const header = new Uint8Array(await file.slice(0, 8).arrayBuffer());
      const isPdf =
        header.length >= 5 &&
        header[0] === 0x25 &&
        header[1] === 0x50 &&
        header[2] === 0x44 &&
        header[3] === 0x46 &&
        header[4] === 0x2d;
      const isPng =
        header.length >= 8 &&
        header[0] === 0x89 &&
        header[1] === 0x50 &&
        header[2] === 0x4e &&
        header[3] === 0x47 &&
        header[4] === 0x0d &&
        header[5] === 0x0a &&
        header[6] === 0x1a &&
        header[7] === 0x0a;
      const isJpeg = header.length >= 3 && header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff;

      return isPdf || isPng || isJpeg;
    } catch {
      return false;
    }
  };

  const fileToBase64Images = async (file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve([dataUrl.split(",")[1]]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const getFileExtension = (fileName: string): string => {
    const ext = fileName.split(".").pop()?.toLowerCase() || "pdf";
    return ext;
  };

  const inferSupplierFromFileName = (fileName: string): string => {
    const base = fileName.replace(/\.[^.]+$/, "");
    const parts = base
      .split(/[_\s]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length === 0) return "";

    let start = 0;
    for (let i = 0; i < parts.length; i++) {
      const token = parts[i].replace(/[.,;:()]/g, "");
      const upper = token.toUpperCase();
      if (/^(?:FRA|FACTURA|N|Nº|NO|NUM|NÚM|NUMERO|NÚMERO)$/.test(upper)) {
        start = i + 1;
        continue;
      }
      if (start > 0 && /^[A-Z0-9][A-Z0-9./-]{2,}$/i.test(token) && /\d/.test(token)) {
        start = i + 1;
        continue;
      }
      if (/^\d{6,8}$/.test(token)) start = i + 1;
    }

    const candidate = parts
      .slice(start)
      .filter((p) => {
        const clean = p.replace(/[.,;:()]/g, "");
        const upper = clean.toUpperCase();
        if (/^(?:CTJ|TJ|CAJA)\d*$/i.test(upper)) return false;
        if (/^\d{4}[A-Z]{3}$/i.test(upper)) return false;
        if (/^R\d{4}[A-Z]{2,3}$/i.test(upper)) return false;
        if (/^\d{1,2}$/.test(clean)) return false;
        return true;
      })
      .join(" ")
      .replace(/\b(S\s*L\s*U?|S\s*A\s*U?|CB|C\s*B)\b\.?/gi, "")
      .replace(/\s+/g, " ")
      .trim();

    return candidate.length >= 3 ? candidate : "";
  };

  const extractStructuredText = (items: any[]): string => {
    // Group text items by Y-coordinate to form rows, then sort by X within each row
    const Y_TOLERANCE = 3;
    const rowMap = new Map<number, any[]>();
    for (const item of items) {
      if (!item.str || !item.str.trim()) continue;
      const y = Math.round((item.transform?.[5] ?? 0) / Y_TOLERANCE) * Y_TOLERANCE;
      if (!rowMap.has(y)) rowMap.set(y, []);
      rowMap.get(y)!.push(item);
    }
    // Sort rows top-to-bottom (descending Y), items left-to-right (ascending X)
    const sortedRows = Array.from(rowMap.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([, rowItems]) =>
        rowItems
          .sort((a: any, b: any) => (a.transform?.[4] ?? 0) - (b.transform?.[4] ?? 0))
          .map((it: any) => it.str.trim())
          .join("  "),
      );
    return sortedRows.join("\n");
  };

  // Recorta márgenes blancos alrededor del contenido para que OCR "ampli-
  // fique" tickets/facturas pequeñas escaneadas dentro de un A4 casi vacío.
  const autoCropWhitespace = (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
  ): HTMLCanvasElement => {
    const { width: w, height: h } = canvas;
    if (w === 0 || h === 0) return canvas;
    let data: Uint8ClampedArray;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch {
      return canvas; // canvas tainted (raro con PDFs), continuar sin crop
    }
    // Un píxel se considera contenido solo si tiene tinta real. Usamos un
    // umbral más estricto y después exigimos densidad por fila/columna para
    // ignorar motas, sombras y bordes del escaneo que antes impedían recortar.
    const THRESH = 190;
    const rowInk = new Uint32Array(h);
    const colInk = new Uint32Array(w);
    // Muestreamos cada 2 píxeles para ir rápido en A4 grandes.
    const step = 2;
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        const idx = (y * w + x) * 4;
        const r = data[idx], g = data[idx + 1], b = data[idx + 2];
        if (r < THRESH || g < THRESH || b < THRESH) {
          rowInk[y] += 1;
          colInk[x] += 1;
        }
      }
    }
    const minRowInk = Math.max(8, Math.round(w * 0.005));
    const minColInk = Math.max(8, Math.round(h * 0.005));
    let top = h, bottom = -1, left = w, right = -1;
    for (let y = 0; y < h; y += step) {
      if (rowInk[y] >= minRowInk) {
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
    for (let x = 0; x < w; x += step) {
      if (colInk[x] >= minColInk) {
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
    if (bottom < 0 || right < 0) return canvas; // hoja totalmente en blanco
    // Añadimos un margen de seguridad para no cortar bordes de letras.
    const pad = Math.round(Math.min(w, h) * 0.04);
    left = Math.max(0, left - pad);
    top = Math.max(0, top - pad);
    right = Math.min(w - 1, right + pad);
    bottom = Math.min(h - 1, bottom + pad);
    const cw = right - left + 1;
    const ch = bottom - top + 1;
    // Si el "recorte" abarca casi toda la página, no compensa recortar.
    if (cw >= w * 0.9 && ch >= h * 0.9) return canvas;
    const out = document.createElement("canvas");
    out.width = cw;
    out.height = ch;
    const octx = out.getContext("2d")!;
    octx.drawImage(canvas, left, top, cw, ch, 0, 0, cw, ch);
    return out;
  };

  const extractFromPdf = async (
    file: File,
    opts?: { preferNative?: boolean },
  ): Promise<{ images: string[]; text: string; isScanned: boolean }> => {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = await getPdfDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    const images: string[] = [];
    const textParts: string[] = [];
    // Always include first 2 pages + last page (where totals usually are)
    const pagesToRead = new Set<number>();
    for (let i = 1; i <= Math.min(pdf.numPages, 2); i++) pagesToRead.add(i);
    pagesToRead.add(pdf.numPages); // always include last page
    const sortedPages = Array.from(pagesToRead).sort((a, b) => a - b);
    // Primera pasada: leer texto embebido de todas las páginas para decidir
    // si es un PDF escaneado (sin capa de texto) y así elevar la resolución.
    const pageTexts: Record<number, string> = {};
    let totalNativeChars = 0;
    for (const i of sortedPages) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent({ includeMarkedContent: false } as any);
      const txt = extractStructuredText(content.items as any[]);
      pageTexts[i] = txt;
      totalNativeChars += txt.replace(/\s+/g, "").length;
    }
    const isScanned = totalNativeChars < 40; // sin texto significativo
    // Cuando el PDF tiene capa de texto se envía el documento nativo a la IA:
    // rasterizarlo degradaba las tablas fiscales (bases y total) de facturas largas.
    const skipImages = Boolean(opts?.preferNative) && !isScanned;
    for (const i of sortedPages) {
      if (skipImages) {
        textParts.push(pageTexts[i] || "");
        continue;
      }
      const page = await pdf.getPage(i);
      const scale = isScanned ? 3.2 : 1.7;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport } as any).promise;
      const finalCanvas = isScanned ? autoCropWhitespace(canvas, ctx) : canvas;
      const quality = isScanned ? 0.92 : 0.8;
      images.push(finalCanvas.toDataURL("image/jpeg", quality).split(",")[1]);
      textParts.push(pageTexts[i] || "");
      canvas.width = 0;
      canvas.height = 0;
      if (finalCanvas !== canvas) {
        finalCanvas.width = 0;
        finalCanvas.height = 0;
      }
    }
    // pdfjs 6.x: use the loading task's destroy() to release the worker.
    await loadingTask.destroy();
    return { images, text: textParts.join("\n---\n"), isScanned };
  };

  const MAX_NATIVE_PDF_BYTES = 8 * 1024 * 1024;

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const comma = result.indexOf(",");
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const addFailed = (file: File, reason: string, sourceId?: string, kind: "rejected" | "error" = "error") => {
    setFailedFiles((prev) => [
      ...prev,
      { id: sourceId || createStableId(getFileStableSeed(file, "failed", `${reason}:${Date.now()}:${Math.random()}`)), file, reason, kind },
    ]);
  };

  const processFile = async (file: File, isRetry = false, sourceId?: string) => {
    setExtracting((n) => n + 1);
    try {
      let images: string[];
      let text: string | undefined;
      let pdfBase64: string | undefined;

      if (isImageFile(file)) {
        images = await fileToBase64Images(file);
        text = undefined;
      } else {
        const canSendNative = file.size <= MAX_NATIVE_PDF_BYTES;
        const result = await extractFromPdf(file, { preferNative: canSendNative });
        images = result.images;
        // PDF con capa de texto: se envía el documento nativo completo. Así la IA
        // lee la tabla fiscal de la última página sin pérdida de resolución.
        if (canSendNative && !result.isScanned) {
          try {
            pdfBase64 = await fileToBase64(file);
          } catch {
            pdfBase64 = undefined;
          }
        }
        // El texto embebido acompaña al documento como referencia. El límite es
        // amplio para que nunca se corte el bloque de totales del final.
        const rawText = result.text.trim();
        text = rawText ? rawText.slice(0, 24000) : undefined;
        // Si no hubo PDF nativo ni imágenes (fallo puntual), rasteriza como antes.
        if (!pdfBase64 && images.length === 0) {
          const fallback = await extractFromPdf(file);
          images = fallback.images;
        }
      }

      const activeReceiverNif = activeReceiverTaxIdRef.current;

      // Reintenta automáticamente ante 429 / errores de red transitorios.
      // No reintenta en 402 (saldo IA agotado), 401 (auth) ni errores no transitorios.
      const invokeWithRetry = async () => {
        const MAX_TRIES = 3;
        let lastResult: { data: any; error: any } = { data: null, error: null };
        let refreshedOnce = false;
        for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
          const result = await supabase.functions.invoke("extract-invoice", {
            body: {
              images,
              text,
              ...(pdfBase64 ? { pdfBase64 } : {}),
              receiverTaxIds: activeReceiverNif ? [activeReceiverNif] : [],
              companyId: appliedCompanyRef.current?.id ?? null,
            },
          });
          lastResult = result;
          if (!result.error) return result;

          const status = (result.error as any)?.context?.status;
          const msg = result.error?.message || "";

          // 401 → probable token expirado/stale. Refresca sesión y reintenta 1 vez.
          if (status === 401 && !refreshedOnce) {
            refreshedOnce = true;
            try { await supabase.auth.refreshSession(); } catch { /* ignore */ }
            continue;
          }

          const isRateLimit = status === 429 || /rate limit/i.test(msg);
          const isNetwork = !status && (/failed to fetch|network|load failed|timed?\s*out/i.test(msg));
          const transient = isRateLimit || isNetwork;
          if (!transient || attempt === MAX_TRIES - 1) return result;

          if (isRateLimit) rateLimitCooldownUntilRef.current = Date.now() + 30_000;

          const base = 1500 * Math.pow(2, attempt);
          const jitter = base * (Math.random() * 0.5 - 0.25);
          await new Promise((r) => setTimeout(r, Math.round(base + jitter)));
        }
        return lastResult;
      };

      const { data, error } = await invokeWithRetry();

      if (error) {
        // Intenta extraer el mensaje real del cuerpo de respuesta (p.ej. 402 saldo IA agotado)
        let detail = error.message || "desconocido";
        try {
          const ctx: any = (error as any).context;
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json();
            if (body?.error) detail = body.error;
          }
        } catch { /* ignore */ }
        const status = (error as any).context?.status;
        if (status === 402 || /payment required/i.test(detail)) {
          detail = "Saldo de IA agotado. Recarga en Ajustes → Cloud & AI balance para continuar procesando facturas.";
        } else if (status === 429 || /rate limit/i.test(detail)) {
          detail = "El servicio de IA está saturado. Vuelve a pulsar «Reintentar» en unos segundos.";
        }
        addFailed(file, `Error del servidor: ${detail}`, sourceId);
        return;
      }
      if (!data) {
        addFailed(file, "Error del servidor: respuesta vacía", sourceId);
        return;
      }


      // Check if document is not an invoice
      // Exceptions: ABONO, Factura Rectificativa, and Albarán when accompanied by Factura
      const documentType = String(data.documentType || "").trim();
      const isAbono = /\babono\b/i.test(documentType);
      const isFacturaRectificativa = /factura\s+rectificativa/i.test(documentType);
      const isAlbaranConFactura = /\balbar[aá]n\b/i.test(documentType) && /\bfactura\b/i.test(documentType);
      const isValidException = isAbono || isFacturaRectificativa || isAlbaranConFactura;
      // Modo tickets de peaje/viaje (activado manualmente): admite solo
      // tickets/facturas simplificadas de peajes, parkings y transporte.
      // Presupuestos, albaranes, pedidos, proformas, etc. siguen rechazándose.
      const NON_INVOICE_BLOCK = /presupuesto|albar[aá]n|pedido|proforma|nota\s+de\s+entrega|oferta|contrato|extracto/i;
      const TICKET_TYPE_RE = /ticket|tique|simplificad|peaje|autopista|autoestrada|parking|aparcamiento|recibo|justificante|comprobante|resguardo|transporte|billete|tr[aá]nsito/i;
      const TICKET_TEXT_RE = /peaje|autopista|autoestrada|concesionaria|parking|aparcamiento|tr[aá]nsito|comprobante\s+de\s+pago/i;
      const isAllowedTicket =
        ticketModeRef.current &&
        !NON_INVOICE_BLOCK.test(documentType) &&
        (TICKET_TYPE_RE.test(documentType) || (!documentType && TICKET_TEXT_RE.test(text || "")));
      if (data.isInvoice === false && !isValidException && !isAllowedTicket) {
        const docType = documentType || "documento desconocido";
        addFailed(file, `Este documento parece ser un ${docType}, no una factura.`, sourceId, "rejected");
        return;
      }

      // Aviso (no bloqueante): la IA leyó como proveedor un NIF que coincide
      // con la empresa receptora activa. Confiamos en la IA, pero marcamos
      // un flag para que el usuario pueda revisar manualmente en el split-view.
      const receiverConfusion = !!data.receiverConfusion;


      const date = data.date || "";
      const rawSupplier = data.supplier || "";
      let invoiceNumber = normalizeInvoiceNumber(data.invoiceNumber || "");
      // Tickets de peaje: si no hay nº de factura, usar ID / nº de tránsito / operación.
      if (!invoiceNumber && isAllowedTicket) {
        const m = (text || "").match(/(?:\bID\b|N[ºo°]?\.?\s*(?:de\s+)?(?:tr[aá]nsito|operaci[oó]n|ticket|transacci[oó]n)|tr[aá]nsito|operaci[oó]n)\s*[:.#]?\s*([A-Z0-9][A-Z0-9\-\/]{4,})/i);
        invoiceNumber = normalizeInvoiceNumber(m?.[1] || (data.ticketId ?? "") || "");
        if (!invoiceNumber) {
          const d = String(data.date || "").replace(/-/g, "");
          invoiceNumber = `TK${d}${Math.round(parseNumericLike(data.totalAmount) * 100)}`;
        }
      }
      const fileNameSupplier = inferSupplierFromFileName(file.name);

      // Apply learned corrections using ALL tax IDs found in document
      // Excluimos solo el NIF de la empresa receptora activa para que el
      // sistema de aprendizaje no lo asocie como proveedor (otros NIFs del
      // grupo sí son válidos como proveedores en facturación intra-grupo).
      const blockedReceiverSet = new Set(activeReceiverNif ? [activeReceiverNif] : []);
      const rawTaxId = (data.supplierTaxId || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
      const originalExtractedTaxId = rawTaxId;
      const originalExtractedSupplier = rawSupplier;
      const allTaxIdsRaw: string[] = Array.isArray(data.allTaxIds) ? data.allTaxIds : [];
      const allTaxIds = allTaxIdsRaw.filter(
        (id) => !blockedReceiverSet.has((id || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase()),
      );
      const correction = findCorrectionByAllTaxIds(allTaxIds, rawTaxId, rawSupplier);
      if (correction) {
        if (correction.correctTaxId) {
          data.supplierTaxId = correction.correctTaxId;
        }
      }
      const normalizeNameForCompare = (value: string) =>
        value
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/\b(SL|SLU|SA|SAU|CB)\b/gi, "")
          .replace(/[^A-Z0-9]+/gi, "")
          .toUpperCase();
      const rawLooksLikeReceiver =
        receiverConfusion &&
        !!fileNameSupplier &&
        !!appliedCompany?.name &&
        normalizeNameForCompare(rawSupplier) === normalizeNameForCompare(appliedCompany.name);
      const supplier = correction
        ? correction.correctName
        : rawLooksLikeReceiver || !rawSupplier.trim()
          ? fileNameSupplier
          : rawSupplier;

      if ((!date || !supplier || !invoiceNumber) && !isRetry) {
        return processFile(file, true, sourceId);
      }

      if (!date || !supplier || !invoiceNumber) {
        const missing = [!date && "fecha", !supplier && "proveedor", !invoiceNumber && "nº factura"]
          .filter(Boolean)
          .join(", ");
        addFailed(file, `No se pudieron extraer: ${missing}.`, sourceId);
        return;
      }

      const formattedDate = date.replace(/-/g, "");
      let paymentMethod = detectPaymentMethod(file.name);
      // Si no se detectó forma de pago pero el nombre contiene 4 dígitos que
      // coinciden con alguna tarjeta configurada en la empresa, lo tratamos
      // como pago con esa tarjeta (p. ej. "..._6029.pdf" → TJ6029).
      if (!paymentMethod) {
        const pa = paymentAccountsRef.current;
        const cards = pa?.cardAccounts ?? [];
        if (cards.length > 0) {
          const nameNoExt = file.name.replace(/\.[^.]+$/, "");
          const tokens = nameNoExt.match(/(?<![0-9])\d{4}(?![0-9])/g) || [];
          for (const tok of tokens) {
            const m = cards.find((c) => c.label.includes(tok) || c.account.includes(tok));
            if (m) { paymentMethod = `TJ${tok}`; break; }
          }
        }
      }
      const ext = getFileExtension(file.name);
      const paymentSuffix = paymentMethod ? `_${paymentMethod}` : "";
      const aiLicensePlate = typeof data.licensePlate === "string" ? normalizeLicensePlate(data.licensePlate) : undefined;
      const fallbackLicensePlate = extractLicensePlateFallback(`${file.name}\n${text || ""}`);
      const licensePlate = aiLicensePlate || fallbackLicensePlate;
      
      const baseName = buildBaseName(formattedDate, supplier, invoiceNumber, paymentSuffix, ext, licensePlate);

      const taxId = data.supplierTaxId || "";

      // Segunda comprobación independiente de la IA: indicios de abono /
      // rectificativa en el texto del propio documento.
      const creditHints = detectCreditNoteHints(text, documentType);
      const isNegative = !!data.isNegativeInvoice || creditHints.strong;
      // Si es abono, los importes SIEMPRE quedan en negativo (independientemente
      // del signo que haya devuelto la IA); si no lo es, se dejan en positivo.
      const signed = (n: number) => {
        const v = Math.round(Math.abs(n) * 100) / 100;
        return isNegative ? -v : v;
      };

      let vatBreakdown = Array.isArray(data.vatBreakdown)
        ? data.vatBreakdown.map((v: any) => ({
            taxableBase: signed(parseNumericLike(v.taxableBase)),
            vatRate: parseNumericLike(v.vatRate),
            vatAmount: signed(parseNumericLike(v.vatAmount)),
          }))
        : [];
      const totalAmount = signed(parseNumericLike(data.totalAmount));
      // Tickets con "IVA incluido" sin base desglosada: separar base e IVA
      // a partir del total (que no se modifica). Tipo leído o 21 % por defecto.
      if (isAllowedTicket && totalAmount !== 0 && !vatBreakdown.some((l) => l.taxableBase !== 0)) {
        const rateMatch = (text || "").match(/IVA[^0-9]{0,15}(\d{1,2})(?:[.,]\d+)?\s*%/i);
        const rate = vatBreakdown[0]?.vatRate || (rateMatch ? Number(rateMatch[1]) : 21);
        const base = Math.round((totalAmount / (1 + rate / 100)) * 100) / 100;
        const vat = Math.round((totalAmount - base) * 100) / 100;
        vatBreakdown = [{ taxableBase: base, vatRate: rate, vatAmount: vat }];
      }
      const irpfAmount = signed(parseNumericLike(data.irpfAmount));

      // Check consistency: ∑Base + ∑IVA - IRPF ≈ Total
      const sumBase = vatBreakdown.reduce((s, l) => s + l.taxableBase, 0);
      const sumVat = vatBreakdown.reduce((s, l) => s + l.vatAmount, 0);
      const calculatedTotal = Math.round((sumBase + sumVat - irpfAmount) * 100) / 100;
      const hasInconsistency = totalAmount !== 0 && Math.abs(totalAmount - calculatedTotal) > 0.05;

      const entry: ProcessedFile = {
        id: sourceId || createStableId(getFileStableSeed(file, "processed", `${Date.now()}:${Math.random()}`)),
        originalName: file.name,
        baseName,
        newName: "",
        date,
        supplier: supplier.trim(),
        invoiceNumber: invoiceNumber.trim(),
        paymentMethod,
        supplierTaxId: taxId,
        originalExtractedTaxId: originalExtractedTaxId || taxId,
        originalExtractedSupplier: originalExtractedSupplier || rawSupplier, // Nombre original de la IA
        vatBreakdown,
        totalAmount,
        irpfRate: parseNumericLike(data.irpfRate),
        irpfAmount,
        supplierAccountCode: "",
        expenseAccountCode: ticketModeRef.current ? "62900000" : "",
        supplierDescription: "",
        isCreditNote: isNegative,
        creditNoteHints: creditHints.matched,
        creditNoteHintLevel: creditHints.strong ? "strong" : creditHints.weak ? "weak" : "none",
        totalInconsistency: hasInconsistency,
        calculatedTotal: hasInconsistency ? calculatedTotal : undefined,
        licensePlate,
        contractNumber: typeof data.contractNumber === "string" ? data.contractNumber.trim() : undefined,
        installmentNumber: typeof data.installmentNumber === "number" && data.installmentNumber > 0 ? data.installmentNumber : undefined,
        leasingAmortization: typeof data.leasingAmortization === "number" && data.leasingAmortization > 0 ? data.leasingAmortization : undefined,
        leasingInterest: typeof data.leasingInterest === "number" && data.leasingInterest > 0 ? data.leasingInterest : undefined,
        receiverConfusion,
        fieldLocations: sanitizeFieldLocations(data.fieldLocations),
        file,
      };

      // Auto-sugerir asiento de pago a partir de la forma de pago detectada
      // y las cuentas configuradas en la empresa aplicada (caja/tarjetas).
      const pa = paymentAccountsRef.current;
      const suggestion = inferPaymentEntryAccount(paymentMethod, pa);
      if (suggestion) {
        entry.generatePaymentEntry = true;
        entry.paymentEntryAccount = suggestion.account;
        entry.paymentEntryKind = suggestion.kind;
      }

      // Check duplicate against contabilizado entries
      entry.isDuplicate = checkDuplicate(
        entry.supplierTaxId || "",
        entry.invoiceNumber,
        file.name,
        entry.supplier,
        currentUserId,
      );

      setProcessed((prev) => {
        const next = recheckAllDuplicates([...prev, entry]);
        return recalculateNames(next, prefixRef.current, startNumberRef.current);
      });
    } catch (e) {
      console.error("Extraction error:", e);
      addFailed(file, `Error inesperado: ${e instanceof Error ? e.message : "desconocido"}`, sourceId);
    } finally {
      setExtracting((n) => n - 1);
    }
  };

  const handleFilesSelect = async (files: File[]) => {
    const supportedChecks = await Promise.all(files.map((file) => isSupportedInvoiceFile(file)));

    const supportedFiles = files.filter((_, index) => supportedChecks[index]);
    const rejectedCount = files.length - supportedFiles.length;

    if (supportedFiles.length === 0) {
      toast({
        title: "No se añadieron archivos",
        description: "Solo se admiten PDF, PNG o JPG/JPEG.",
        variant: "destructive",
      });
      return;
    }

    // Detección de duplicados en tiempo real (por nombre y por tamaño exacto).
    // Cubre tres orígenes: pendientes, ya procesados y ya contabilizados.
    const existingByName = new Set<string>();
    const existingBySig = new Set<string>(); // name|size
    pendingFiles.forEach((p) => {
      existingByName.add(p.file.name);
      existingBySig.add(`${p.file.name}|${p.file.size}`);
    });
    processed.forEach((p) => {
      existingByName.add(p.originalName);
      if (p.file) existingBySig.add(`${p.originalName}|${p.file.size}`);
    });
    failedFiles.forEach((p) => {
      if (p.file) {
        existingByName.add(p.file.name);
        existingBySig.add(`${p.file.name}|${p.file.size}`);
      }
    });
    const contabilizados = getContabilizados(currentUserId);
    contabilizados.forEach((c) => {
      existingByName.add(c.fileName);
      if (c.file) existingBySig.add(`${c.fileName}|${c.file.size}`);
    });

    const duplicateNames: string[] = [];
    supportedFiles.forEach((f) => {
      const sig = `${f.name}|${f.size}`;
      if (existingBySig.has(sig) || existingByName.has(f.name)) {
        duplicateNames.push(f.name);
      }
    });

    const batchId = `${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    const filesWithId = supportedFiles.map((file, index) => ({
      id: createStableId(getFileStableSeed(file, "pending", `${batchId}:${index}`)),
      file,
    }));
    setPendingFiles((prev) => [...prev, ...filesWithId]);

    toast({
      title: `${supportedFiles.length} archivo${supportedFiles.length > 1 ? "s" : ""} añadido${supportedFiles.length > 1 ? "s" : ""}`,
      description: "Pulsa «Procesar con IA» cuando estés listo.",
    });

    if (duplicateNames.length > 0) {
      const preview = duplicateNames.slice(0, 3).join(", ");
      const more = duplicateNames.length > 3 ? ` y ${duplicateNames.length - 3} más` : "";
      toast({
        title: `⚠️ ${duplicateNames.length} posible${duplicateNames.length > 1 ? "s" : ""} duplicado${duplicateNames.length > 1 ? "s" : ""}`,
        description: `Ya existen archivos con el mismo nombre en pendientes, procesados o contabilizados: ${preview}${more}. Revísalos antes de procesar.`,
        variant: "destructive",
      });
    }

    if (rejectedCount > 0) {
      toast({
        title: `${rejectedCount} archivo${rejectedCount > 1 ? "s" : ""} omitido${rejectedCount > 1 ? "s" : ""}`,
        description: "Formato no compatible (usa PDF, PNG o JPG/JPEG).",
        variant: "destructive",
      });
    }
  };

  const removePendingFile = (pendingId: string) => {
    setPendingFiles((prev) => prev.filter((f) => f.id !== pendingId));
  };

  const handleStartProcessing = () => {
    if (pendingFiles.length === 0) return;
    const filesToProcess = [...pendingFiles];
    setTotalToProcess(filesToProcess.length);
    // Mark all as processing (visible with spinner)
    setProcessingIds(new Set(filesToProcess.map((f) => f.id)));
    toast({
      title: `Procesando ${filesToProcess.length} factura${filesToProcess.length > 1 ? "s" : ""}…`,
      description: "Extrayendo datos con IA.",
    });

    // Concurrencia adaptativa: para lotes grandes (≥10) bajamos de 3 → 2
    // para dejar margen al gateway de IA cuando hay varios usuarios trabajando.
    const baseConcurrency = filesToProcess.length >= 10 ? 2 : 3;
    let cursor = 0;
    const next = async (workerIdx: number) => {
      // Jitter de arranque para no disparar las N llamadas en el mismo ms.
      await new Promise((r) => setTimeout(r, Math.round(Math.random() * 400) * workerIdx));
      while (cursor < filesToProcess.length) {
        // Si estamos en cooldown por 429 previo, sólo un worker sigue activo.
        if (workerIdx > 0 && Date.now() < rateLimitCooldownUntilRef.current) {
          await new Promise((r) => setTimeout(r, 2000));
          continue;
        }
        const idx = cursor++;
        const pf = filesToProcess[idx];
        try {
          await processFile(pf.file, false, pf.id);
        } catch (e) {
          console.error("Unhandled processFile error:", e);
          addFailed(pf.file, `Error inesperado: ${e instanceof Error ? e.message : "desconocido"}`, pf.id);
        } finally {
          // Remove from pending & processing after completion
          setPendingFiles((prev) => prev.filter((f) => f.id !== pf.id));
          setProcessingIds((prev) => {
            const s = new Set(prev);
            s.delete(pf.id);
            return s;
          });
        }
      }
    };
    const workerCount = Math.min(baseConcurrency, filesToProcess.length);
    const workers = Array.from({ length: workerCount }, (_, i) => next(i));
    Promise.all(workers).catch((e) => {
      console.error("Processing batch error:", e);
      toast({
        title: "Error en el procesamiento",
        description: "Algunos archivos no se pudieron procesar.",
        variant: "destructive",
      });
    });
  };

  const handleRetryFailed = (failedId: string) => {
    const failed = failedFiles.find((f) => f.id === failedId);
    if (!failed) return;
    setFailedFiles((prev) => prev.filter((f) => f.id !== failedId));
    setTotalToProcess(1);
    processFile(failed.file, false, failed.id);
  };

  const handleRetryAllFailed = () => {
    const toRetry = [...failedFiles];
    setFailedFiles([]);
    setTotalToProcess(toRetry.length);
    toRetry.forEach((f) => processFile(f.file, false, f.id));
  };

  const zipDownloadInFlightRef = useRef(false);
  const zipDownloadButtonRef = useRef<HTMLButtonElement | null>(null);

  const setZipDownloadUiState = (isActive: boolean) => {
    zipDownloadInFlightRef.current = isActive;

    const button = zipDownloadButtonRef.current;
    if (!button) return;

    button.disabled = isActive;
    button.setAttribute("aria-busy", isActive ? "true" : "false");
  };

  const handleDownloadAll = () => {
    if (zipDownloadInFlightRef.current || processed.length === 0) return;
    setZipDownloadUiState(true);

    // Capture data synchronously before going async.
    // Excluimos facturas pendientes de validar (documentos "no procesados" promovidos que el usuario no ha guardado aún).
    // Los anexos se descargan junto a su factura (memoria: user request).
    const usedNames = new Set<string>();
    const sourceFiles = processed
      .filter((pf) => !pf.pendingValidation)
      .flatMap((pf) => {
        const invoiceName = sanitizeFileName(pf.newName);
        const base = invoiceName.replace(/\.[^.]+$/, "");
        const annexes = (pf.attachments ?? []).map((f, i) => {
          let name = sanitizeFileName(`${base}_anexo_${f.name}`);
          if (usedNames.has(name)) name = sanitizeFileName(`${base}_anexo_${i + 1}_${f.name}`);
          usedNames.add(name);
          return { name, file: f, notes: undefined as string | undefined };
        });
        usedNames.add(invoiceName);
        return [{ name: invoiceName, file: pf.file, notes: pf.notes }, ...annexes];
      });
    const zipName = `facturas_${prefix || "iva"}_${Date.now()}.zip`;

    // Run entirely outside React's lifecycle (memory: error-handling-strategy)
    window.setTimeout(async () => {
      try {
        const { stampNoteOnDocument } = await import("@/lib/stampNote");
        const filesToZip = await Promise.all(
          sourceFiles.map(async (it) => ({
            name: it.name,
            file: it.notes ? await stampNoteOnDocument(it.file, it.notes) : it.file,
          })),
        );
        await downloadFilesAsZip(filesToZip, zipName);
      } catch (e) {
        if (typeof console !== "undefined") console.error("ZIP error:", e);
      } finally {
        try { setZipDownloadUiState(false); } catch (_) { /* */ }
      }
    }, 0);
  };

  // Descarga de los archivos rechazados (no procesados).
  // Siempre en ZIP (memoria: pdf-download-as-zip) para evitar el bloqueo MOTW.
  const handleDownloadFailedFile = (ff: FailedFile) => {
    void downloadFileAsZip(ff.file, ff.file.name);
  };

  const handleDownloadAllFailed = () => {
    if (failedFiles.length === 0) return;
    const items = failedFiles.map((f) => ({ file: f.file, name: f.file.name }));
    void downloadFilesAsZip(items, `rechazadas_${Date.now()}.zip`);
  };

  const handleFinalize = async () => {
    setPendingFiles([]);
    setProcessed([]);
    setTicketMode(false);
    setFailedFiles([]);
    setTrashedFiles([]);
    setTotalToProcess(0);
    setPrefix("");
    setStartNumber(1);
    setSelectedProcessedIds(new Set());
    setSearchQuery("");
    setAppliedCompany(null);
    supplierMasterRef.current = null;
    activeReceiverTaxIdRef.current = "";
    await clearSession();
  };

  const handleSignOut = async () => {
    clearContabilizados(currentUserId);
    await clearSession();
    await signOut();
  };

  // isProcessing is declared earlier (telemetry hook)

  const statusCounts = useMemo(() => {
    const pending = processed.filter((f) => f?.pendingValidation).length;
    return { all: processed.length, pending, ready: processed.length - pending };
  }, [processed]);

  const visibleProcessedFiles = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const byStatus = processed.filter((file) => {
      if (statusFilter === "pending") return !!file?.pendingValidation;
      if (statusFilter === "ready") return !file?.pendingValidation;
      return true;
    });
    if (!query) return byStatus;

    return byStatus.filter(
      (file) =>
        toSafeText(file?.newName).toLowerCase().includes(query) ||
        toSafeText(file?.supplier).toLowerCase().includes(query) ||
        toSafeText(file?.supplierTaxId).toLowerCase().includes(query) ||
        toSafeText(file?.invoiceNumber).toLowerCase().includes(query) ||
        toSafeText(file?.originalName).toLowerCase().includes(query),
    );
  }, [processed, searchQuery, statusFilter]);


  /** Facturas ya validadas por el usuario (excluye documentos promovidos desde "No procesados" que aún no se han guardado). */
  const validatedProcessed = useMemo(
    () => processed.filter((f) => !f.pendingValidation),
    [processed],
  );

  /** Items normalizados para el buscador global (mismo componente que Emitidas). */
  const globalSearchItems = useMemo<SearchableItem[]>(
    () =>
      validatedProcessed.map((f) => ({
        id: f.id,
        title: toSafeText(f.newName) || toSafeText(f.originalName),
        entityName: toSafeText(f.supplier),
        taxId: toSafeText(f.supplierTaxId),
        subtitleParts: [f.supplier, f.supplierTaxId, f.invoiceNumber, f.licensePlate, f.date],
        totalAmount: typeof f.totalAmount === "number" ? f.totalAmount : null,
        searchValue: [f.newName, f.originalName, f.supplier, f.supplierTaxId, f.invoiceNumber, f.licensePlate, f.date]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      })),
    [validatedProcessed],
  );


  if (sessionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Cargando sesión…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-primary">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-6 py-4">
          <Link to="/" className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand-green))]">
            <FileStack className="h-4 w-4 text-white" />
          </Link>
          <div className="flex-1">
            <h1 className="text-base font-bold text-primary-foreground">Procesador de facturas recibidas</h1>
            <p className="text-xs text-primary-foreground/60">Ferreiro & Portelo</p>
          </div>
          {appliedCompany && (
            <button
              type="button"
              onClick={() => {
                if (pendingFiles.length + processed.length + failedFiles.length > 0) {
                  setShowActiveCompanySelector(true);
                } else {
                  setShowActiveCompanySelector(true);
                }
              }}
              className="flex items-center gap-2 rounded-md border border-primary-foreground/20 bg-primary/40 px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary/60 transition-colors"
              title="Cambiar empresa activa"
            >
              <Building2 className="h-3.5 w-3.5" />
              <span className="font-medium">{appliedCompany.name}</span>
              {appliedCompany.tax_id && (
                <span className="text-primary-foreground/60">· {appliedCompany.tax_id}</span>
              )}
              <span className="ml-1 text-primary-foreground/70 underline">Cambiar</span>
            </button>
          )}
          <Button variant="outline" size="sm" className="gap-2 text-xs" onClick={() => navigate("/historico-facturas")}>
            <FileStack className="h-3.5 w-3.5" />
            Histórico facturas
          </Button>
          <Button variant="outline" size="sm" className="gap-2 text-xs" onClick={() => setShowMasterSelector(true)} data-tour="company-selector">
            Datos por empresa
          </Button>
          {isHydrated && (
            <MasterSelector
              open={showMasterSelector}
              onSelect={(company) => {
                setShowMasterSelector(false);
                navigate(`/proveedores/${company.id}`);
              }}
              onCancel={() => setShowMasterSelector(false)}
            />
           )}
          {/* Selector que fija el cliente ACTIVO de la sesión */}
          {isHydrated && (
            <MasterSelector
              open={showActiveCompanySelector}
              onSelect={(company) => {
                setShowActiveCompanySelector(false);
                const hasFiles =
                  pendingFiles.length + processed.length + failedFiles.length > 0;
                const isSame = appliedCompany?.id === company.id;
                if (isSame) return;
                if (hasFiles && appliedCompany) {
                  setPendingCompanyChange(company);
                } else {
                  void handleApplyMaster(company);
                }
              }}
              onCancel={() => setShowActiveCompanySelector(false)}
            />
          )}
          <AlertDialog
            open={pendingCompanyChange !== null}
            onOpenChange={(v) => !v && setPendingCompanyChange(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cambiar de empresa</AlertDialogTitle>
                <AlertDialogDescription>
                  Tienes {pendingFiles.length + processed.length + failedFiles.length} factura
                  {pendingFiles.length + processed.length + failedFiles.length === 1 ? "" : "s"} en
                  la sesión actual de <strong>{appliedCompany?.name}</strong>. Cambiar a{" "}
                  <strong>{pendingCompanyChange?.name}</strong> reiniciará la sesión y eliminará
                  esas facturas.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setPendingCompanyChange(null)}>
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    const target = pendingCompanyChange;
                    setPendingCompanyChange(null);
                    if (!target) return;
                    try {
                      await clearSession();
                    } catch (e) {
                      console.error("clearSession failed:", e);
                    }
                    setPendingFiles([]);
                    setProcessed([]);
                    setFailedFiles([]);
                    setPrefix("");
                    setStartNumber(1);
                    setSelectedProcessedIds(new Set());
                    void handleApplyMaster(target);
                  }}
                >
                  Cambiar y reiniciar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {(processed.length > 0 || failedFiles.length > 0) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleFinalize}
              className="gap-2 text-xs text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <CheckCircle className="h-3 w-3" />
              Finalizar
            </Button>
          )}
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSignOut}
            className="text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary/80"
            title="Cerrar sesión"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="px-6 py-8">
        <div className="mx-auto max-w-5xl grid gap-8 lg:grid-cols-5">
          {/* Left: Upload & Settings */}
          <div className="lg:col-span-2">
            <div className="sticky top-8 space-y-6">
              <div>
                <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Subir facturas
                </h2>
                {!appliedCompany ? (
                  <div className="rounded-lg border-2 border-dashed border-primary/30 bg-accent/40 p-6 text-center space-y-3">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        Selecciona la empresa que vas a tramitar
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Permite a la IA distinguir correctamente al emisor del receptor en cada factura.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="gap-2"
                      onClick={() => setShowActiveCompanySelector(true)}
                    >
                      <Building2 className="h-4 w-4" />
                      Elegir empresa
                    </Button>
                  </div>
                ) : (
                  <div data-tour="upload">
                    <DropZone onFilesSelect={handleFilesSelect} disabled={isProcessing} />
                    <label className="mt-3 flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 accent-primary"
                        checked={ticketMode}
                        disabled={isProcessing}
                        onChange={(e) => setTicketMode(e.target.checked)}
                      />
                      <span>
                        <span className="font-medium text-foreground">Modo tickets de peaje / viaje (gasto 62900000)</span>
                        <span className="block text-xs text-muted-foreground">
                          Admite tickets de peajes, parkings y transporte. Presupuestos, albaranes, pedidos y proformas se siguen rechazando. Desactívalo al terminar.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                {/* Pending files list */}
                {pendingFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground">
                      {pendingFiles.length} archivo{pendingFiles.length > 1 ? "s" : ""} en cola:
                    </p>
                  </div>
                    <div className="max-h-40 space-y-1 overflow-y-auto">
                      {pendingFiles.map((f) => {
                        const isFileProcessing = processingIds.has(f.id);
                        return (
                          <div
                            key={f.id}
                            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-xs ${
                              isFileProcessing ? "border border-primary/20 bg-accent" : "bg-muted"
                            }`}
                          >
                            {isFileProcessing ? (
                              <Loader2 className="h-3 w-3 shrink-0 animate-spin text-primary" />
                            ) : (
                              <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                            )}
                            <span className="min-w-0 flex-1 truncate text-foreground">{f.file.name}</span>
                            {isFileProcessing ? (
                              <span className="shrink-0" aria-live="polite">
                                <span className="text-[10px] font-medium text-primary">Procesando…</span>
                              </span>
                            ) : (
                              <>
                                <button
                                  onClick={() => removePendingFile(f.id)}
                                  className="shrink-0 text-muted-foreground hover:text-destructive"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Processing progress bar */}
                {isProcessing &&
                  totalToProcess > 0 &&
                  (() => {
                    const completedCount = totalToProcess - pendingFiles.length;
                    const pct = Math.round((completedCount / totalToProcess) * 100);
                    return (
                      <div className="mt-3 space-y-1.5">
                        <div
                          className="flex items-center justify-between text-xs text-muted-foreground"
                          aria-live="polite"
                        >
                          <span className="flex items-center gap-1.5">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span>
                              Procesando {completedCount} de {totalToProcess}
                            </span>
                          </span>
                          <span className="font-medium text-foreground">{pct}%</span>
                        </div>
                        <Progress value={pct} className="h-2" />
                      </div>
                    );
                  })()}
              </div>

              <div className="space-y-4">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Numeración</h2>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="prefix">Serie</Label>
                    <Input
                      id="prefix"
                      placeholder="Ej: A, FP-"
                      value={prefix}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        setPrefix(val);
                        setProcessed((prev) => recalculateNames(prev, val, startNumber));
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="startNumber">Nº de IVA</Label>
                    <Input
                      id="startNumber"
                      type="number"
                      min={1}
                      value={startNumber}
                      onChange={(e) => {
                        const n = Math.max(1, parseInt(e.target.value) || 1);
                        setStartNumber(n);
                        setProcessed((prev) => recalculateNames(prev, prefix, n));
                      }}
                    />
                  </div>
                </div>
                {allSeries.filter((s) => !s.isMain).map((s, i) => (
                  <div key={`extra-${i}`} className="grid grid-cols-[1fr_1fr_auto] items-end gap-3">
                    <div className="space-y-1">
                      <Label htmlFor={`extra-prefix-${i}`} className="text-xs">Serie {i + 2}</Label>
                      <Input
                        id={`extra-prefix-${i}`}
                        placeholder="Ej: B"
                        defaultValue={s.prefix}
                        onBlur={(e) => {
                          const v = e.target.value.trim().toUpperCase();
                          if (v && v !== s.prefix) handleEditExtraSeries(i, { prefix: v });
                        }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`extra-start-${i}`} className="text-xs">Nº de IVA ({s.count} fras.)</Label>
                      <Input
                        id={`extra-start-${i}`}
                        type="number"
                        min={1}
                        value={s.start}
                        onChange={(e) => handleEditExtraSeries(i, { start: parseInt(e.target.value) || 1 })}
                      />
                    </div>
                    <Button variant="ghost" size="icon" aria-label={`Quitar serie ${s.prefix}`} onClick={() => handleRemoveExtraSeries(i)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    const used = new Set(allSeries.map((x) => x.prefix));
                    const letter = "BCDEFGHIJKLMNOPQRSTUVWXYZ".split("").find((l) => !used.has(l)) ?? "Z";
                    persistExtraSeries([...allSeries.filter((x) => !x.isMain).map((x) => ({ prefix: x.prefix, start: x.start })), { prefix: letter, start: 1 }]);
                  }}
                >
                  + Añadir serie
                </Button>
                {allSeries.length > 1 && (
                  <p className="text-xs text-muted-foreground">
                    Todas las facturas entran en la serie principal. Cámbialas desde la pestaña «Serie» al revisar cada PDF.
                  </p>
                )}


                <Button
                  onClick={handleStartProcessing}
                  disabled={pendingFiles.length === 0 || isProcessing}
                  className="w-full gap-2 bg-[hsl(var(--brand-green))] text-white hover:bg-[hsl(152,40%,45%)]"
                  size="lg"
                >
                  <span className="inline-flex items-center gap-2" aria-live="polite" aria-atomic="true">
                    <span className="inline-flex items-center gap-2">
                      {isProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                      <span>{isProcessing ? "Procesando…" : `Procesar con IA (${pendingFiles.length})`}</span>
                    </span>
                  </span>
                </Button>
              </div>
            </div>
          </div>

          {/* Right: Results */}
          <div className="lg:col-span-3 space-y-8">
            {/* Failed files section */}
            {failedFiles.length > 0 && (
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    No procesadas ({failedFiles.length})
                  </h2>
                  <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadAllFailed}
                    className="gap-2 text-xs"
                  >
                    <Download className="h-3 w-3" />
                    Descargar ZIP
                  </Button>
                  {failedFiles.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRetryAllFailed}
                      disabled={isProcessing}
                      className="gap-2 text-xs"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Reintentar todas
                    </Button>
                  )}
                  </div>
                </div>
                <div className="space-y-2">
                  {failedFiles.map((ff) => (
                    <div key={ff.id} className="rounded-lg border border-destructive/20 bg-destructive/5 p-3">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedFailedIds.has(ff.id)}
                          onCheckedChange={() => handleToggleFailedSelection(ff.id)}
                          className="mt-0.5 shrink-0"
                          aria-label={`Seleccionar ${ff.file.name} para unir`}
                        />
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground">{ff.file.name}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">{ff.reason}</p>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadFailedFile(ff)}
                            className="gap-1 text-xs h-7 px-2"
                            title="Descargar"
                            aria-label={`Descargar ${ff.file.name}`}
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFailedViewerFile(ff.file)}
                            className="gap-1 text-xs h-7 px-2"
                            title="Ver archivo"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => promoteFailedToProcessed(ff)}

                            disabled={isProcessing}
                            className="gap-1 text-xs h-7 px-2"
                            title="Editar manualmente"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRetryFailed(ff.id)}
                            disabled={isProcessing}
                            className="gap-1 text-xs h-7 px-2"
                          >
                            <RotateCcw className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteFailed(ff.id)}
                            className="gap-1 text-xs h-7 px-2 hover:text-destructive"
                            title="Desechar"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {ff.editing && (
                        <div className="mt-3 space-y-2 border-t border-destructive/10 pt-3">
                          <div className="grid grid-cols-3 gap-2">
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Fecha *
                              </label>
                              <Input
                                value={ff.editDate || ""}
                                onChange={(e) => updateFailedField(ff.id, "editDate", e.target.value)}
                                placeholder="YYYY-MM-DD"
                                className="h-7 text-xs"
                                autoFocus
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Proveedor *
                              </label>
                              <Input
                                value={ff.editSupplier || ""}
                                onChange={(e) => updateFailedField(ff.id, "editSupplier", e.target.value)}
                                className="h-7 text-xs"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Nº Factura *
                              </label>
                              <Input
                                value={ff.editInvoiceNumber || ""}
                                onChange={(e) => updateFailedField(ff.id, "editInvoiceNumber", e.target.value)}
                                className="h-7 text-xs"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                NIF
                              </label>
                              <Input
                                value={ff.editSupplierTaxId || ""}
                                onChange={(e) => updateFailedField(ff.id, "editSupplierTaxId", e.target.value)}
                                placeholder="B12345678"
                                className="h-7 text-xs font-mono"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Base Imp.
                              </label>
                              <Input
                                value={ff.editTaxableBase || ""}
                                onChange={(e) => updateFailedField(ff.id, "editTaxableBase", e.target.value)}
                                placeholder="0.00"
                                className="h-7 text-xs"
                                type="number"
                                step="0.01"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                % IVA
                              </label>
                              <Input
                                value={ff.editVatRate || ""}
                                onChange={(e) => updateFailedField(ff.id, "editVatRate", e.target.value)}
                                placeholder="21"
                                className="h-7 text-xs"
                                type="number"
                                step="0.01"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Cuota IVA
                              </label>
                              <Input
                                value={ff.editVatAmount || ""}
                                onChange={(e) => updateFailedField(ff.id, "editVatAmount", e.target.value)}
                                placeholder="0.00"
                                className="h-7 text-xs"
                                type="number"
                                step="0.01"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                % IRPF
                              </label>
                              <Input
                                value={ff.editIrpfRate || ""}
                                onChange={(e) => updateFailedField(ff.id, "editIrpfRate", e.target.value)}
                                placeholder="0"
                                className="h-7 text-xs"
                                type="number"
                                step="0.01"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Cuota IRPF
                              </label>
                              <Input
                                value={ff.editIrpfAmount || ""}
                                onChange={(e) => updateFailedField(ff.id, "editIrpfAmount", e.target.value)}
                                placeholder="0.00"
                                className="h-7 text-xs"
                                type="number"
                                step="0.01"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                Total
                              </label>
                              <Input
                                value={ff.editTotalAmount || ""}
                                onChange={(e) => updateFailedField(ff.id, "editTotalAmount", e.target.value)}
                                placeholder="0.00"
                                className="h-7 text-xs"
                                type="number"
                                step="0.01"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                                F. Pago
                              </label>
                              <Input
                                value={ff.editPaymentMethod || ""}
                                onChange={(e) => updateFailedField(ff.id, "editPaymentMethod", e.target.value)}
                                placeholder="CAJA, TARJETA…"
                                className="h-7 text-xs"
                              />
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => cancelEditingFailed(ff.id)}
                              className="h-7 px-2 text-xs"
                            >
                              <X className="h-3 w-3 mr-1" /> Cancelar
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => validateFailed(ff)}
                              disabled={!ff.editDate || !ff.editSupplier || !ff.editInvoiceNumber}
                              className="h-7 px-2 text-xs bg-[hsl(var(--brand-green))] hover:bg-[hsl(152,40%,45%)] text-white"
                            >
                              <Check className="h-3 w-3 mr-1" /> Validar
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {isHydrated && (
              <div key="failed-files-overlays">
                <FileViewer
                  file={failedViewerFile}
                  open={failedViewerFile !== null}
                  onClose={() => setFailedViewerFile(null)}
                />
                <FailedFileSplitView
                  file={failedFiles.find((f) => f.id === splitViewFailedId) ?? null}
                  open={splitViewFailedId !== null}
                  onClose={() => {
                    setSplitViewFailedId(null);
                    if (splitViewFailedId) cancelEditingFailed(splitViewFailedId);
                  }}
                  onFieldChange={updateFailedField}
                  onValidate={(ff, vatLines) => {
                    validateFailed(ff, vatLines);
                    setSplitViewFailedId(null);
                  }}
                  onDiscard={handleDeleteFailed}
                />
              </div>
            )}

            <div>
              <div className="mb-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    Facturas procesadas ({processed.length})
                  </h2>
                  {processed.length > 1 && (
                    <Button
                      ref={zipDownloadButtonRef}
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadAll}
                      className="gap-2"
                      data-tour="download-zip"
                    >
                      <Download className="h-3.5 w-3.5" />
                      <span>Descargar ZIP</span>
                    </Button>
                  )}
                </div>
                {processed.length > 0 && (
                  <>
                    <DocStatusFilter value={statusFilter} onChange={setStatusFilter} counts={statusCounts} />
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar por nombre, CIF o nº factura…"
                        className="h-8 pl-9 text-xs"
                      />
                    </div>
                  </>
                )}

              </div>
              <div data-tour="processed-list">
                <InvoiceList
                  files={visibleProcessedFiles}
                  onUpdate={handleUpdateFile}
                  onDelete={handleDeleteProcessed}
                  onUpdateNumericField={handleUpdateNumericField}
                  onAddVatLine={handleAddVatLine}
                  onRemoveVatLine={handleRemoveVatLine}
                  openFileId={openInvoiceId}
                  onSplitViewClose={handleSplitViewClose}
                  selectedIds={selectedProcessedIds}
                  onToggleSelect={handleToggleSelectProcessed}
                  onToggleSelectAll={handleToggleSelectAllProcessed}
                  appliedCompanyId={appliedCompany?.id ?? null}
                  onUpdateExpenseAccount={handleUpdateExpenseAccount}
                  onUpdateSupplierAccount={handleUpdateSupplierAccount}
                  onUpdateVatBookField={handleUpdateVatBookField}
                  onUpdateDescription={handleUpdateDescription}
                  onQuickSupplierCreated={handleQuickSupplierCreated}
                  paymentAccounts={paymentAccounts}
                  onUpdatePaymentEntry={handleUpdatePaymentEntry}
                  onUpdateOperationType={handleUpdateOperationType}
                  onUpdateRetentionType={handleUpdateRetentionType}
                  onUpdateVatDeductibility={handleUpdateVatDeductibility}
                  onToggleCreditNote={handleToggleCreditNote}
                  onSplit={handleOpenSplitter}
                  onMergeWith={handleMergeWith}
                  onUpdateNotes={handleUpdateNotes}
                  onUpdateAttachments={handleUpdateAttachments}
                  seriesOptions={seriesOptions}
                  onChangeSeries={handleChangeSeries}
                />
                <div className="mt-4">
                  <InvoiceTrash
                    trashedFiles={trashedFiles}
                    onRestoreProcessed={handleRestoreProcessed}
                    onRestoreFailed={handleRestoreFailed}
                    onPermanentDelete={handlePermanentDelete}
                    onEmptyTrash={handleEmptyTrash}
                  />
                </div>
              </div>
            </div>

            {/* VAT Book moved outside grid for full width */}
          </div>
        </div>

        {/* VAT Book — full width */}
        {processed.length > 0 && (
          <div className="mt-8" data-tour="vat-book">
            <VatBook
              files={validatedProcessed}
              prefix={prefix}
              startNumber={startNumber}
              appliedCompany={appliedCompany}
              saveStatus={saveStatus}
              lastSavedAt={lastSavedAt}
              onUpdateExpenseAccount={handleUpdateExpenseAccount}
              onUpdateSupplierAccount={handleUpdateSupplierAccount}
              onUpdateVatBookField={handleUpdateVatBookField}
              onUpdateNumericField={handleUpdateNumericField}
              onUpdateDescription={handleUpdateDescription}
              onApplyMaster={handleApplyMaster}
              onAddVatLine={handleAddVatLine}
              onRemoveVatLine={handleRemoveVatLine}
            />
          </div>
        )}
      </main>
      <GlobalSearch
        open={globalSearchOpen}
        onOpenChange={setGlobalSearchOpen}
        items={globalSearchItems}
        entityLabel="proveedor"
        onSelectItem={(id) => {
          setOpenInvoiceId(id);
          setGlobalSearchOpen(false);
        }}
      />

      <BulkActionsBar
        selectedCount={selectedProcessedIds.size + selectedFailedIds.size}
        onApplyPatch={handleBulkApplyPatch}
        onDelete={handleBulkDelete}
        onMerge={handleBulkMerge}
        onClear={handleClearSelection}
        existingSeries={listSeries(processed, prefix, startNumber)}
      />

      <AIValidatorDemo processed={validatedProcessed} onOpenInvoice={setOpenInvoiceId} />
      <PdfSplitterDialog
        file={splitterFile?.file ?? null}
        open={splitterFile !== null}
        onClose={() => setSplitterFile(null)}
        onConfirm={handleSplitConfirm}
      />
      <PdfMergeOrderDialog
        files={mergeCandidates ?? []}
        open={mergeCandidates !== null}
        onClose={() => { setMergeCandidates(null); setMergeTargetId(null); }}
        onConfirm={handleConfirmMerge}
      />

    </div>
  );
};

export default Index;
